import { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { api } from '../api/client';
import { resolveSignerLine, toSetNorm, setsEqual, toSetKey, parseReviewFilter, buildQueueUrl, buildRequestUrl, isGateEligibleBase, gateHoldReasons } from './convex-utils';
import {
  ReviewBadge, FeedbackChip, WhatChangedPanel, FeedbackSection, UnmatchedFeedbackBucket,
  reviewVerdict, autoStamp, bindFeedback,
  type FeedbackEntry, type ReviewHistoryEntry,
} from './convex-review';

// ── Types ────────────────────────────────────────────────────────────────────

interface ConvexRequest {
  request_id: string;
  received_at: string;
  email_subject: string;
  email_from: string;
  email_body?: string;
  email_message_id?: string;
  triage_class: 'A' | 'B' | 'C' | 'D';
  triage_reason?: string;
  status: string;
  extracted_reference_number?: string;
  extracted_insured_name?: string;
  extraction_confidence?: string;
  extraction_notes?: string;
  policy_data?: any;
  rendered_at?: string;
  parsed_at?: string;
  registry_preview?: any;
  fh_claim_data?: any;
  template_s3_key?: string;
  pdf_s3_key?: string;
  template_download_url?: string;
  download_is_pdf?: boolean;
  attachment_s3_keys?: string[];
  reviewer_notes?: string;
  approved_by?: string;
  approved_at?: string;
  send_result?: any;
  extracted_reference_numbers?: string[];
  multi_ref_note?: string;
  parse_error?: string;
  insurer?: string;
  insurer_name?: string;
  queue_type?: string;
  classifier_confidence?: string;
  classifier_reasoning?: string;
  archived?: boolean;
  archived_at?: string;
  archived_by?: string;
  archived_reason?: string;
  delivery_status?: 'failed' | 'delivered' | 'unknown';
  delivery_failures?: {
    recipient: string;
    ndr_code: string;
    ndr_reason: string;
    ndr_received_at: string;
    ndr_message_id: string;
  }[];
  send_attempts?: SendAttempt[];
  deleted_manually?: boolean;
  deleted_by?: string;
  deleted_at?: string;
  latest_attempt_n?: number;
  latest_delivery_status?: string;
  archive_log?: {
    action: string;
    user_id: string;
    user_name: string;
    timestamp: string;
    previous_triage_class?: string;
    previous_status?: string;
  };
  request_state?: 'complete' | 'no_record' | 'needs_review';
  auto_verified?: string;
  auto_verified_at?: string;
  skippy_verified?: boolean | string;
  skippy_verified_at?: string;
  recipient_verified?: boolean;
  recipient_verified_at?: string;
  recipient_verified_reason?: string;
  response_recipients?: {
    to: { email: string; name: string; type: string }[];
    cc: { email: string; name: string; type: string }[];
    extraction_method?: string;
    extraction_failed?: boolean;
  };
  outbound_email?: {
    subject: string;
    body: string;
    to: string[];
    cc: string[];
  };
  broker_name?: string;
  recipients_original_to?: string[];
  recipients_original_cc?: string[];
  recipients_corrected_at?: string;
  recipients_corrected_by?: string;
  recipients_correction_reason?: string;
  email_body_text?: string;
  duplicate_hold?: boolean;
  duplicate_hold_at?: string;
  duplicate_hold_by?: string;
  duplicate_hold_evidence?: {
    prior_sent_at?: string;
    prior_message_id?: string;
    prior_internet_message_id?: string;
    prior_request_id?: string;
    recipient_to?: string[];
  };
  // Amos-64 review layer (audit metadata only)
  last_reviewed_at?: string;
  last_reviewed_by?: string;
  review_history?: ReviewHistoryEntry[];
  prior_delivery_detected_at?: string;
  nil_guard_demoted_at?: string;
  recipient_guard_at?: string;
  freshness_hold_at?: string;
}

interface MasterUpload {
  upload_id: string;
  uploaded_at: string;
  filename: string;
  s3_key: string;
  status: string;
  row_count: number;
  policy_count?: number;
}

interface SendAttempt {
  attempt_id: string;
  attempt_n: number;
  attempted_at: string;
  send_result: any;
  delivery_status: string;
  delivery_failures: ConvexRequest['delivery_failures'];
  recipients: any;
  operator_modifications?: any;
}

interface AttemptRow {
  request: ConvexRequest;
  attempt: SendAttempt;
  attemptCount: number;
  groupSortKey: string;
}

function getAttemptsView(req: ConvexRequest): SendAttempt[] {
  if (req.send_attempts && req.send_attempts.length > 0) return req.send_attempts;
  const sr = req.send_result;
  if (!sr) return [];
  return [{
    attempt_id: req.request_id,
    attempt_n: 1,
    attempted_at: sr.timestamp || req.approved_at || '',
    send_result: sr,
    delivery_status: req.delivery_status || (sr.queued ? 'queued' : 'unknown'),
    delivery_failures: req.delivery_failures || [],
    recipients: req.response_recipients || {},
    operator_modifications: {},
  }];
}

function requestIdToHue(requestId: string): number {
  let hash = 0;
  for (let i = 0; i < requestId.length; i++) {
    hash = ((hash << 5) - hash + requestId.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 360;
}

function getAttemptDeliveryState(att: SendAttempt): DeliveryState | null {
  if (!att.send_result?.sent) return null;
  if (att.delivery_status === 'failed') return 'failed';
  if (att.delivery_status === 'delivered') return 'delivered';
  const ts = att.attempted_at;
  if (ts) {
    const age = Date.now() - new Date(ts).getTime();
    if (age > 24 * 60 * 60 * 1000 && !att.delivery_status) return 'legacy';
  }
  return 'queued';
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function relativeTime(value: string): string {
  const now = Date.now();
  const then = new Date(value).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const TRIAGE_CONFIG = {
  A: { label: 'A - Manual', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  B: { label: 'B - Review', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  C: { label: 'C - Ready', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
  D: { label: 'D - Completed', bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', dot: 'bg-slate-500' },
} as const;

function TriageBadge({ triage }: { triage: 'A' | 'B' | 'C' | 'D' }) {
  const cfg = TRIAGE_CONFIG[triage] || TRIAGE_CONFIG.A;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    new: 'bg-blue-100 text-blue-700',
    parsing: 'bg-blue-100 text-blue-700',
    parsed: 'bg-slate-100 text-slate-700',
    approved: 'bg-green-100 text-green-700',
    sent: 'bg-green-200 text-green-800',
    parse_failed: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-slate-100 text-slate-600'}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

type DeliveryState = 'queued' | 'failed' | 'delivered' | 'legacy';

function getDeliveryState(req: ConvexRequest): DeliveryState | null {
  if (req.status !== 'sent' || !req.send_result?.sent) return null;
  if (req.delivery_status === 'failed') return 'failed';
  if (req.delivery_status === 'delivered') return 'delivered';
  // Stale threshold: records >24h old with no delivery_status → "legacy"
  const sendTimestamp = req.send_result?.timestamp || req.approved_at;
  if (sendTimestamp) {
    const sendTime = new Date(sendTimestamp).getTime();
    const age = Date.now() - sendTime;
    if (age > 24 * 60 * 60 * 1000 && !req.delivery_status) return 'legacy';
  }
  return 'queued';
}

const DELIVERY_CONFIG: Record<DeliveryState, { label: string; className: string }> = {
  queued:    { label: 'Queued',          className: 'bg-blue-100 text-blue-700' },
  failed:   { label: 'Delivery Failed', className: 'bg-red-100 text-red-700' },
  delivered:{ label: 'Delivered',        className: 'bg-green-100 text-green-700' },
  legacy:   { label: 'Sent (legacy)',    className: 'bg-slate-100 text-slate-500' },
};

function DeliveryBadge({ request }: { request: ConvexRequest }) {
  const state = getDeliveryState(request);
  if (!state) return null;
  const cfg = DELIVERY_CONFIG[state];
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function AttemptDeliveryBadge({ attempt }: { attempt: SendAttempt }) {
  const state = getAttemptDeliveryState(attempt);
  if (!state) return null;
  const cfg = DELIVERY_CONFIG[state];
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function AttemptChip({ n, total }: { n: number; total: number }) {
  return (
    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
      Attempt {n} of {total}
    </span>
  );
}

function DeliveryFailurePanel({ failures }: { failures: ConvexRequest['delivery_failures'] }) {
  const [expanded, setExpanded] = useState(false);
  if (!failures || failures.length === 0) return null;
  return (
    <div className="mt-3 pt-3 border-t border-red-200">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900"
      >
        <svg className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {failures.length} failed recipient{failures.length > 1 ? 's' : ''} — click for details
      </button>
      {expanded && (
        <div className="mt-2 space-y-2">
          {failures.map((f, i) => (
            <div key={i} className="bg-white border border-red-100 rounded-md px-3 py-2 text-xs">
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <span className="font-medium text-slate-500">Recipient</span>
                <span className="text-slate-800 font-mono">{f.recipient}</span>
                <span className="font-medium text-slate-500">NDR Code</span>
                <span className="text-red-700 font-mono">{f.ndr_code}</span>
                <span className="font-medium text-slate-500">Reason</span>
                <span className="text-slate-700">{f.ndr_reason}</span>
                <span className="font-medium text-slate-500">NDR Received</span>
                <span className="text-slate-600">{f.ndr_received_at ? `${formatDate(f.ndr_received_at)} ${formatTime(f.ndr_received_at)}` : '--'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const INSURER_COLORS: Record<string, string> = {
  convex: 'bg-blue-100 text-blue-700',
  amlin: 'bg-purple-100 text-purple-700',
  ufg: 'bg-teal-100 text-teal-700',
  brit: 'bg-indigo-100 text-indigo-700',
  other: 'bg-slate-100 text-slate-600',
  unknown: 'bg-orange-100 text-orange-700',
};

function InsurerBadge({ insurer, name }: { insurer?: string; name?: string }) {
  if (!insurer) return null;
  const color = INSURER_COLORS[insurer] || INSURER_COLORS.unknown;
  const label = name || insurer.charAt(0).toUpperCase() + insurer.slice(1);
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

// Interim frontend-only render gate — not access control.
// Backend enforcement pending fix/convex-send-env-cdk-pin.
// Amos-57: this remains a UI *render* gate only — membership here decides who
// SEES the button, never what the button may release. The real guarantee is
// server-side: approve_request independently re-runs the C-lane gate (403) and
// the full W1c predicate (409) at entry, so a tampered or stale bundle cannot
// push a non-C or termless record through. Backend enforcement of *who* may
// bulk-approve is still pending (see fix/convex-send-env-cdk-pin).
// Added dorellas@ per Richard 2026-07-27 (Amos-57). Removed shelle@
// (b438c488-…) in the same change — that Cognito account is disabled.
const BULK_APPROVE_AUTHORIZED_SUBS = [
  '24a8e408-f031-70c8-fcde-51da83288a58', // shelleh@trans11claims.com
  '6488c458-3061-7054-c548-1a01c01e8de1', // richard@trans11claims.com
  '0488e4e8-b0f1-7060-a27f-604a4ebf5fae', // dorellas@trans11claims.com
];

const BULK_APPROVE_DELAY_MS = 4500; // ~12/min, under 30/min Exchange EOP ceiling

// isGateEligibleBase + gateHoldReasons relocated to convex-utils.ts (Amos-69)
// so the de-gate behavior is directly Jest-testable without importing this
// page module. Semantics unchanged apart from the de-gate itself.

// W1c mirror (amos34 W2): the server refuses approve (409) when the record's
// re-render inputs are unprovable — _w1c_approve_block_reasons in
// lambda/convex/handler.py. The UI must never advertise "Gate Ready" for a
// record the server will refuse. The LIST endpoint's projection omits
// policy_data / rendered_at / parsed_at, so list rows are verified via a
// per-record detail fetch (w1cMap below); the detail page evaluates inline.
const W1C_TERM_KEYS = ['p1_term', 'p2_term', 'p3_term', 'p4_term', 'p5_term', 'p6_term'];

function w1cBlockReasons(req: ConvexRequest): string[] {
  const reasons: string[] = [];
  if (!(req.rendered_at || req.parsed_at)) {
    reasons.push('render freshness unprovable');
  }
  let pd: any = req.policy_data;
  if (typeof pd === 'string') {
    try { pd = JSON.parse(pd); } catch { pd = null; }
  }
  if (!(pd && typeof pd === 'object' && W1C_TERM_KEYS.some(k => pd[k]))) {
    reasons.push('missing policy terms — approve would be refused (409)');
  }
  return reasons;
}

type W1cVerdict = { ready: boolean; reasons: string[] };

// Full eligibility. On the detail page (full record in hand) call with no
// second arg — W1c evaluates inline. On list rows pass the w1cMap entry;
// a missing entry means the detail fetch hasn't landed yet and the record
// is NOT eligible (fail closed, never advertise unverified).
function isGateEligible(req: ConvexRequest, w1c?: W1cVerdict | 'pending'): boolean {
  if (!isGateEligibleBase(req)) return false;
  if (w1c === 'pending') return false;
  if (w1c) return w1c.ready;
  // No verdict supplied (detail page): evaluate inline — but only when the
  // record actually carries the fields; a projected list row without them is
  // never eligible (fail closed).
  if (!('policy_data' in req || req.rendered_at || req.parsed_at)) return false;
  return w1cBlockReasons(req).length === 0;
}

// Amos-69: the dup tag is informational, never gating. Rendered alongside the
// gate badge for any record carrying dup evidence — including records migrated
// to duplicate_hold=false (degated_by=amos69), whose evidence is preserved.
function RepeatChip({ req }: { req: ConvexRequest }) {
  if (!req.duplicate_hold && !req.duplicate_hold_evidence) return null;
  const priorDate = req.duplicate_hold_evidence?.prior_sent_at
    ? formatDate(req.duplicate_hold_evidence.prior_sent_at)
    : '';
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600"
      title={`This reference was previously answered${priorDate ? ` on ${priorDate}` : ''}. Informational only — every request gets a response.`}
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
      Repeat — prior response{priorDate ? ` ${priorDate}` : ''}
    </span>
  );
}

function GateEligibilityBadge({ req, w1c }: { req: ConvexRequest; w1c?: W1cVerdict | 'pending' }) {
  return (
    <>
      <RepeatChip req={req} />
      <GateBadgeInner req={req} w1c={w1c} />
    </>
  );
}

function GateBadgeInner({ req, w1c }: { req: ConvexRequest; w1c?: W1cVerdict | 'pending' }) {
  if (req.triage_class !== 'C') {
    if (req.auto_verified === 'zero_claims') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Zero Claims
        </span>
      );
    }
    return null;
  }
  // amos34 W2: "Gate Ready" only when the base predicate AND the server's W1c
  // predicate both pass. While the list view's detail fetch is in flight the
  // badge says so instead of guessing (fail closed).
  if (isGateEligibleBase(req) && w1c === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500"
            title="Verifying render inputs against the server's approve gate (W1c)">
        <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Checking gate…
      </span>
    );
  }
  if (isGateEligible(req, w1c)) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        Gate Ready
      </span>
    );
  }
  const w1cReasons = w1c && w1c !== 'pending' ? w1c.reasons
    : (req.rendered_at || req.parsed_at || req.policy_data) ? w1cBlockReasons(req) : [];
  const reasons = [...gateHoldReasons(req), ...w1cReasons];
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700" title={reasons.join(', ')}>
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
      Held{reasons.length > 0 ? `: ${reasons.join(', ')}` : ''}
    </span>
  );
}

function QueueTypeBadge({ queueType }: { queueType?: string }) {
  if (!queueType || queueType === 'convex_auto') return null;
  const label = queueType === 'known_manual' ? 'Manual — Known' : 'Manual — Unknown';
  const color = queueType === 'known_manual' ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-orange-50 text-orange-700 border border-orange-200';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

// ── Sub-navigation ───────────────────────────────────────────────────────────

function ConvexNav() {
  const tabs = [
    { to: '/convex', label: 'Request Queue', end: true },
    { to: '/convex/master', label: 'Master File' },
  ];

  return (
    <div className="border-b border-slate-200 mb-6">
      <nav className="flex gap-4">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              [
                'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                isActive
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
              ].join(' ')
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  REQUEST QUEUE
// ═══════════════════════════════════════════════════════════════════════════════

function RequestQueue() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ['all', 'convex', 'manual', 'A', 'B', 'C', 'D'];
  const [activeTab, setActiveTab] = useState<string>(validTabs.includes(searchParams.get('tab') || '') ? searchParams.get('tab')! : 'all');
  const [requests, setRequests] = useState<ConvexRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [queueSearch, setQueueSearch] = useState('');
  const [dCount, setDCount] = useState(0);
  // Rider #7 (amos28): whether the currently-displayed list is a truncated page
  // and what the true total is, as reported by the backend.
  const [listTruncated, setListTruncated] = useState(false);
  const [listTotal, setListTotal] = useState(0);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bulkResolving, setBulkResolving] = useState(false);
  const [bulkResolveProgress, setBulkResolveProgress] = useState({ done: 0, total: 0 });
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, errors: 0 });
  const [showZeroClaimsOnly, setShowZeroClaimsOnly] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [highlightedRequestId, setHighlightedRequestId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ requestId: string; attemptCount: number } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const bulkCancelRef = useRef(false);
  const [userSub, setUserSub] = useState<string | null>(null);
  const [bulkExcluded, setBulkExcluded] = useState<ConvexRequest[]>([]);

  useEffect(() => {
    fetchAuthSession()
      .then((session) => {
        const sub = session.tokens?.idToken?.payload?.sub as string | undefined;
        setUserSub(sub || null);
      })
      .catch(() => setUserSub(null));
  }, []);

  const isBulkApproveAuthorized = userSub != null && BULK_APPROVE_AUTHORIZED_SUBS.includes(userSub);

  // amos34 W2: per-record W1c verdicts for list rows. The list projection omits
  // policy_data / rendered_at / parsed_at, so every base-eligible row gets a
  // detail fetch and a server-mirroring W1c evaluation. Missing entry = pending
  // = NOT eligible (fail closed).
  const [w1cMap, setW1cMap] = useState<Record<string, W1cVerdict>>({});
  useEffect(() => {
    const candidates = requests.filter(
      r => r.status === 'parsed' && isGateEligibleBase(r) && !(r.request_id in w1cMap)
    );
    if (candidates.length === 0) return;
    let cancelled = false;
    (async () => {
      const CONC = 4;
      for (let i = 0; i < candidates.length; i += CONC) {
        if (cancelled) return;
        const batch = candidates.slice(i, i + CONC);
        const results = await Promise.allSettled(
          batch.map(r => api.getConvexRequest(r.request_id))
        );
        if (cancelled) return;
        setW1cMap(prev => {
          const next = { ...prev };
          results.forEach((res, idx) => {
            const id = batch[idx].request_id;
            if (res.status === 'fulfilled') {
              const reasons = w1cBlockReasons(res.value as ConvexRequest);
              next[id] = { ready: reasons.length === 0, reasons };
            } else {
              // Fetch failed: record stays non-eligible with a stated reason
              // rather than silently pending forever.
              next[id] = { ready: false, reasons: ['gate check failed to load — retry by refreshing'] };
            }
          });
          return next;
        });
      }
    })();
    return () => { cancelled = true; };
  }, [requests]);  // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { key: 'all', label: 'All' },
    { key: 'convex', label: 'Convex' },
    { key: 'manual', label: 'Manual Queue' },
    { key: 'A', label: 'A - Manual', color: 'text-red-600' },
    { key: 'B', label: 'B - Review', color: 'text-amber-600' },
    { key: 'C', label: 'C - Ready', color: 'text-green-600' },
    { key: 'D', label: 'D - Completed', color: 'text-slate-500' },
  ];

  const fetchRequests = useCallback(async () => {
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (activeTab === 'convex') {
        params.queue_type = 'convex_auto';
      } else if (activeTab === 'manual') {
        // Fetch all and filter client-side for both manual types
      } else if (['A', 'B', 'C', 'D'].includes(activeTab)) {
        params.triage_class = activeTab;
      }
      if (queueSearch.trim()) {
        params.search = queueSearch.trim();
      }
      if (activeTab === 'D' && showDeleted) {
        params.include_deleted = 'true';
      }
      const data = await api.getConvexRequests(
        Object.keys(params).length > 0 ? params : undefined
      );
      let items = data.requests || [];
      if (activeTab === 'manual') {
        items = items.filter((r: ConvexRequest) =>
          r.queue_type === 'known_manual' || r.queue_type === 'unknown_manual'
        );
      }
      // Rider #7 (amos28): the backend now states when the list it returned is
      // partial. Record it — bulk approve refuses to act on a truncated list
      // rather than silently operating on whatever happened to fit in the page.
      setListTruncated(Boolean(data.truncated));
      setListTotal(typeof data.count === 'number' ? data.count : items.length);
      setRequests(items);
    } catch (err: any) {
      setError(err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [activeTab, queueSearch, showDeleted]);

  useEffect(() => {
    setLoading(true);
    fetchRequests();
  }, [fetchRequests]);

  // Fetch all counts on initial load + D count separately
  const [allRequests, setAllRequests] = useState<ConvexRequest[]>([]);
  useEffect(() => {
    api.getConvexRequests().then((d: any) => setAllRequests(d.requests || [])).catch(() => {});
    api.getConvexRequests({ triage_class: 'D' }).then((d: any) => setDCount(d.count || 0)).catch(() => {});
  }, []);

  // Rider #7 (amos28): these badges are computed by filtering `allRequests`, which
  // is itself one capped page (the `else` branch of list_requests, limit=2000 per
  // status). The backend returns a truthful Select=COUNT total, which this
  // discards — the same "truthful count + truncated items" split that hid the
  // dup-guard page cap. Not live today (C=78), and fixing it properly means a
  // per-class count endpoint rather than filtering an array client-side; the
  // immediate safety property (never bulk-act on a partial list) is enforced in
  // handleBulkApprove instead. Left as a rider, stated rather than silent.
  const countFor = (key: string) => {
    if (key === 'D') return dCount;
    if (key === 'all') return allRequests.length;
    if (key === 'convex') return allRequests.filter(r => r.queue_type === 'convex_auto' || (!r.queue_type && r.insurer !== 'unknown')).length;
    if (key === 'manual') return allRequests.filter(r => r.queue_type === 'known_manual' || r.queue_type === 'unknown_manual').length;
    return allRequests.filter(r => r.triage_class === key).length;
  };

  const gateEligibleCount = requests.filter(
    r => r.status === 'parsed' && isGateEligible(r, w1cMap[r.request_id] ?? 'pending')
  ).length;

  // Amos-64 review layer: B/C tabs default to hiding Reviewed records; the
  // one-click All toggle restores everything (nothing hidden irrecoverably —
  // counts on the toggle always show the full total).
  const [reviewFilter, setReviewFilter] = useState<'new' | 'all'>(parseReviewFilter(searchParams.get('filter')));
  const reviewFilterActive = ['B', 'C'].includes(activeTab);
  const tabRows = requests.filter(r => !['A', 'B', 'C'].includes(activeTab) || r.triage_class === activeTab);
  const reviewedCount = reviewFilterActive ? tabRows.filter(r => reviewVerdict(r).state === 'reviewed').length : 0;

  const [feedbackByRequest, setFeedbackByRequest] = useState<Record<string, FeedbackEntry[]>>({});
  const [unmatchedFeedback, setUnmatchedFeedback] = useState<FeedbackEntry[]>([]);
  useEffect(() => {
    api.getConvexFeedback()
      .then((d: any) => {
        const bound = bindFeedback((d.items || []) as FeedbackEntry[]);
        setFeedbackByRequest(bound.byRequest);
        setUnmatchedFeedback(bound.unmatched);
      })
      .catch(() => {});
  }, []);

  const handleBulkApprove = async () => {
    // Rider #7 (amos28): FAIL CLOSED ON A TRUNCATED LIST.
    // `requests` is one page from the backend. If the backend says the list is
    // partial, every downstream count here (eligible set, dedup groups, sibling
    // detection) is computed over an arbitrary subset — and would look perfectly
    // normal while silently skipping records. Refuse rather than act on it.
    if (listTruncated) {
      window.alert(
        `Bulk approve unavailable — the queue list is truncated ` +
        `(showing ${requests.length} of ${listTotal}).\n\n` +
        `Acting on a partial list would silently skip records. Narrow the view ` +
        `with a search, or approve individually.`
      );
      return;
    }
    const allEligible = requests.filter(
      r => r.status === 'parsed' && isGateEligible(r, w1cMap[r.request_id] ?? 'pending')
    );
    if (allEligible.length === 0) return;

    // Group all eligible by reference for dedup + sibling detection.
    const refGroups = new Map<string, ConvexRequest[]>();
    for (const req of allEligible) {
      const ref = req.extracted_reference_number || req.request_id;
      const group = refGroups.get(ref) || [];
      group.push(req);
      refGroups.set(ref, group);
    }

    // Pick one per ref (most recent received_at). Remainder are siblings.
    const deduped: ConvexRequest[] = [];
    const siblingsByRef = new Map<string, ConvexRequest[]>();
    Array.from(refGroups.entries()).forEach(([ref, group]) => {
      const sorted = [...group].sort((a, b) => b.received_at.localeCompare(a.received_at));
      deduped.push(sorted[0]);
      if (sorted.length > 1) {
        siblingsByRef.set(ref, sorted.slice(1));
      }
    });

    // Fetch detail for deduped + all siblings (need TO recipients for both).
    const allToResolve = [...deduped, ...Array.from(siblingsByRef.values()).flat()];
    setBulkResolving(true);
    setBulkResolveProgress({ done: 0, total: allToResolve.length });
    const detailMap = new Map<string, ConvexRequest>();
    const CONCURRENCY = 5;
    let resolvedCount = 0;
    for (let i = 0; i < allToResolve.length; i += CONCURRENCY) {
      const batch = allToResolve.slice(i, i + CONCURRENCY);
      const results = await Promise.allSettled(
        batch.map(r => api.getConvexRequest(r.request_id))
      );
      for (let j = 0; j < results.length; j++) {
        if (results[j].status === 'fulfilled') {
          const detail = (results[j] as PromiseFulfilledResult<ConvexRequest>).value;
          detailMap.set(detail.request_id, detail);
        }
      }
      resolvedCount += batch.length;
      setBulkResolveProgress({ done: resolvedCount, total: allToResolve.length });
    }
    // amos34 W2: W1c re-check on the FRESH details (belt over the w1cMap
    // braces — the map may be stale if the record changed since it was built).
    // Any candidate the server would 409 is dropped from this pass, stated.
    const w1cDropped: ConvexRequest[] = [];
    for (const req of [...deduped]) {
      const detail = detailMap.get(req.request_id);
      if (detail && w1cBlockReasons(detail).length > 0) {
        w1cDropped.push(req);
        const di = deduped.indexOf(req);
        if (di >= 0) deduped.splice(di, 1);
      }
    }
    // Selection rule (b): exclude any deduped record whose ref+TO matches
    // an already-sent record in D. Defense-in-depth for failed stamp passes.
    //
    // W4' (amos28) — PAGE-CAP FIX. This previously pulled the whole D bucket via
    // getConvexRequests({triage_class:'D'}), which lands on the Lambda's
    // `elif triage_class:` branch and is capped at limit=2000 (handler.py:4674)
    // with ScanIndexForward=False. With 3,898 D records the guard silently saw
    // only the newest 2,000 and dropped the oldest 1,898 (48.7%) — while the UI
    // count stayed truthful via a separate Select=COUNT, so the truncation was
    // invisible. XSC001319's prior serve (received 2026-05-11, older than the
    // ~2026-05-26 window boundary) fell outside it and the re-serve went out,
    // despite that record carrying a perfect ref+TO key all along.
    //
    // Fix: query D **per eligible ref** using the search param, which routes to
    // the `viewing_d_bucket and search` branch (handler.py:4654-4669) — server-side
    // FilterExpression, limit=50000, no page cap. Uses the EXISTING route, so no
    // new API Gateway resource (the stack is at 499/500 and cdk deploy is barred).
    // `search` is a `contains` match, so results are a superset — we re-filter to
    // an exact ref match client-side rather than trusting the server's contains.
    const eligibleRefs = new Set<string>();
    allEligible.forEach(r => eligibleRefs.add(r.extracted_reference_number || ''));
    eligibleRefs.delete('');

    // W5' (amos28) — FAIL-CLOSED. Evidence-gathering failures previously fell
    // open: the D fetch used `.catch(() => [])`, silently disabling rule-b
    // entirely, and detail fetches used allSettled/fulfilled-only, silently
    // dropping any prior serve whose fetch was throttled. Both defaulted toward
    // SENDING on missing evidence. Now every failure is recorded per-ref and the
    // affected records are blocked from this pass rather than waved through.
    const evidenceFailedRefs = new Set<string>();
    const evidenceFailedRequestIds = new Set<string>();

    const dSentByRef = new Map<string, ConvexRequest[]>();
    const refList = Array.from(eligibleRefs);
    for (let i = 0; i < refList.length; i += CONCURRENCY) {
      const batch = refList.slice(i, i + CONCURRENCY);
      const results = await Promise.allSettled(
        batch.map(ref => api.getConvexRequests({ triage_class: 'D', search: ref }))
      );
      results.forEach((result, idx) => {
        const ref = batch[idx];
        if (result.status !== 'fulfilled') {
          // Cannot prove this ref is un-served -> do not send it.
          evidenceFailedRefs.add(ref);
          return;
        }
        const rows: ConvexRequest[] = ((result.value as any)?.requests || []) as ConvexRequest[];
        rows.forEach((d: ConvexRequest) => {
          if (d.status !== 'sent' && d.status !== 'approved' && d.status !== 'completed') return;
          // Exact ref match — `search` is a permissive `contains`.
          if ((d.extracted_reference_number || '') !== ref) return;
          const group = dSentByRef.get(ref) || [];
          group.push(d);
          dSentByRef.set(ref, group);
        });
      });
    }

    // Detail-fetch overlapping D records to get their TO recipients.
    const sentToKeys = new Map<string, Set<string>>();
    // ref|toKey -> the matching D (already-sent) record, retained so a
    // rule-b skip can be disposed with real evidence (Amos-20 A7).
    const sentEvidenceByRefTo = new Map<string, ConvexRequest>();
    const dToFetch = Array.from(dSentByRef.values()).flat();
    for (let i = 0; i < dToFetch.length; i += CONCURRENCY) {
      const batch = dToFetch.slice(i, i + CONCURRENCY);
      const results = await Promise.allSettled(
        batch.map(r => api.getConvexRequest(r.request_id))
      );
      results.forEach((result, idx) => {
        if (result.status !== 'fulfilled') {
          // W5': a prior-serve record we know exists but cannot read. Its ref is
          // unprovable this pass -> fail closed.
          const failedRef = batch[idx].extracted_reference_number || '';
          if (failedRef) evidenceFailedRefs.add(failedRef);
          return;
        }
        const d = result.value as ConvexRequest;
        const ref = d.extracted_reference_number || '';
        const tk = toSetKey(d.response_recipients?.to);
        if (ref && tk) {
          const keys = sentToKeys.get(ref) || new Set<string>();
          keys.add(tk);
          sentToKeys.set(ref, keys);
          const evidenceKey = `${ref}::${tk}`;
          const existing = sentEvidenceByRefTo.get(evidenceKey);
          const dSentAt = d.approved_at || d.send_result?.timestamp || d.received_at || '';
          const existingSentAt = existing ? (existing.approved_at || existing.send_result?.timestamp || existing.received_at || '') : '';
          if (!existing || dSentAt > existingSentAt) {
            sentEvidenceByRefTo.set(evidenceKey, d);
          }
        } else if (ref && !tk) {
          // A prior serve with no readable TO cannot be compared at ref+TO level.
          evidenceFailedRefs.add(ref);
        }
      });
    }

    setBulkResolving(false);

    // Rule (b) detection — ref+TO already sent. Amos-69 (Richard ruling
    // 2026-08-05): detection STAYS (it writes the dup annotation — audit
    // trail + double-fire tripwire) but no longer excludes the record from
    // the pass. Every request gets a response (standing rule 08-04).
    const ruleBDetected: ConvexRequest[] = [];
    const ruleBEvidenceMatch = new Map<string, ConvexRequest>(); // request_id -> matched D record
    const dedupedAfterRuleB: ConvexRequest[] = [];
    // W5': records blocked because their prior-serve evidence could not be established.
    const evidenceBlocked: ConvexRequest[] = [];
    for (const req of deduped) {
      const detail = detailMap.get(req.request_id);
      if (!detail) {
        // W5': the record's own detail fetch failed — previously `continue`, which
        // dropped it silently from every downstream tally. Surface it instead.
        evidenceFailedRequestIds.add(req.request_id);
        evidenceBlocked.push(req);
        continue;
      }
      const ref = detail.extracted_reference_number || '';
      const tk = toSetKey(detail.response_recipients?.to);
      if (evidenceFailedRefs.has(ref)) {
        evidenceBlocked.push(detail);
        continue;
      }
      const sentKeys = sentToKeys.get(ref);
      if (sentKeys && tk && sentKeys.has(tk)) {
        ruleBDetected.push(detail);
        const match = sentEvidenceByRefTo.get(`${ref}::${tk}`);
        if (match) ruleBEvidenceMatch.set(detail.request_id, match);
      }
      // Amos-69: detected repeats CONTINUE into the pass (annotated below).
      dedupedAfterRuleB.push(detail);
    }

    // W5': hard-stop the pass if any record's prior-serve evidence is unprovable.
    // Never silent-proceed: the operator decides with the failure in front of them.
    if (evidenceBlocked.length > 0) {
      const blockedRefs = evidenceBlocked
        .map(r => r.extracted_reference_number || r.request_id)
        .join(', ');
      window.alert(
        `Bulk approve halted — duplicate-send evidence could not be verified for ` +
        `${evidenceBlocked.length} record(s):\n\n${blockedRefs}\n\n` +
        `These records were NOT sent, and nothing was written. The prior-serve lookup ` +
        `failed (network/throttle), so the guard cannot prove they have not already ` +
        `been served. Retry the bulk approve; if it persists, send these individually ` +
        `after checking their history.`
      );
      return;
    }

    // Plan the dup ANNOTATION for every rule-b detection (Amos-69: the write
    // is the audit trail — the record still sends in this pass). Evidence is
    // assembled here for the confirm dialog, but NOTHING is written until the
    // user confirms the pass below — cancel = zero writes.
    const duplicateHoldPlanned = ruleBDetected.map(req => {
      const match = ruleBEvidenceMatch.get(req.request_id);
      const toEmails = (req.response_recipients?.to || []).map(r => r.email);
      const evidence = match
        ? {
            prior_sent_at: match.approved_at || match.send_result?.timestamp || match.received_at || '',
            prior_message_id: match.send_result?.tracking_id || '',
            prior_internet_message_id: match.send_result?.internet_message_id || match.email_message_id || '',
            prior_request_id: match.request_id,
            recipient_to: toEmails,
          }
        : { recipient_to: toEmails };
      return { request_id: req.request_id, evidence };
    });
    let duplicateHoldCount = 0;
    let duplicateHoldFailCount = 0;

    // Guardrail on DETAIL data (has response_recipients).
    const clean: ConvexRequest[] = [];
    const excluded: ConvexRequest[] = [];
    for (const req of dedupedAfterRuleB) {
      const toList = req.response_recipients?.to?.map(r => r.email) || [];
      const recipientsEmpty = toList.length === 0;
      const isSenderFallback = req.response_recipients?.extraction_method === 'sender_fallback';
      const fromDomain = req.email_from?.split('@')[1]?.toLowerCase();
      const externalTo = toList.filter(e => e.split('@')[1]?.toLowerCase() !== fromDomain);
      const requesterEcho = toList.length > 0 && externalTo.length === 0;
      if (recipientsEmpty || isSenderFallback || requesterEcho) {
        excluded.push(req);
      } else {
        clean.push(req);
      }
    }
    setBulkExcluded(excluded);

    if (clean.length === 0) {
      window.alert(`All ${excluded.length} record(s) require manual review (recipient issues). Use single approve.`);
      return;
    }

    // Pre-classify siblings by TO match for the confirm dialog.
    let totalSiblingsToMark = 0;
    let totalConflictSiblings = 0;
    for (const req of clean) {
      const ref = req.extracted_reference_number || req.request_id;
      const siblings = siblingsByRef.get(ref) || [];
      const sentTo = toSetNorm(detailMap.get(req.request_id)?.response_recipients?.to);
      for (const sib of siblings) {
        const sibDetail = detailMap.get(sib.request_id);
        const sibTo = toSetNorm(sibDetail?.response_recipients?.to);
        if (setsEqual(sentTo, sibTo)) {
          totalSiblingsToMark++;
        } else {
          totalConflictSiblings++;
        }
      }
    }

    const excludedMsg = excluded.length > 0
      ? `\n\n${excluded.length} record(s) excluded for recipient issues — handle via single approve.`
      : '';
    const dedupMsg = allEligible.length !== deduped.length
      ? ` (deduped from ${allEligible.length} to ${deduped.length} unique references)`
      : '';
    const siblingMsg = totalSiblingsToMark > 0
      ? `\n${totalSiblingsToMark} duplicate sibling(s) will be auto-archived.`
      : '';
    const conflictMsg = totalConflictSiblings > 0
      ? `\n${totalConflictSiblings} sibling(s) with distinct broker contacts — left eligible.`
      : '';
    const ruleBMsg = duplicateHoldPlanned.length > 0
      ? `\n${duplicateHoldPlanned.length} repeat(s) of prior sends — sending per standing rule (dup evidence annotated).`
      : '';
    const w1cMsg = w1cDropped.length > 0
      ? `\n${w1cDropped.length} record(s) dropped — server approve gate (W1c) would refuse them.`
      : '';
    if (!window.confirm(`Approve and send ${clean.length} gate-eligible requests?${dedupMsg}${siblingMsg}${conflictMsg}${ruleBMsg}${excludedMsg}${w1cMsg}`)) return;

    // User confirmed the pass — write the dup annotations BEFORE the sends
    // (the annotation endpoint refuses records already sent/approved).
    if (duplicateHoldPlanned.length > 0) {
      const dhResults = await Promise.allSettled(
        duplicateHoldPlanned.map(p => api.duplicateHoldConvexRequest(p.request_id, p.evidence))
      );
      duplicateHoldCount = dhResults.filter(r => r.status === 'fulfilled').length;
      duplicateHoldFailCount = dhResults.filter(r => r.status === 'rejected').length;
    }

    setBulkApproving(true);
    bulkCancelRef.current = false;
    setBulkProgress({ done: 0, total: clean.length, errors: 0 });

    let done = 0;
    let errors = 0;
    const stampedSiblings: string[] = [];
    const stampFailures: { request_id: string; error: string }[] = [];
    const conflictRefs: { ref: string; sibling_id: string; sent_to: string[]; sibling_to: string[] }[] = [];

    for (const req of clean) {
      if (bulkCancelRef.current) break;

      // Idempotency: skip if already approved/sent (resume after browser drop)
      if (req.status === 'approved' || req.status === 'sent' || req.status === 'completed') {
        done++;
        setBulkProgress({ done, total: clean.length, errors });
        continue;
      }

      let sendOk = false;
      try {
        await api.approveConvexRequest(req.request_id);
        sendOk = true;
      } catch {
        errors++;
      }
      done++;
      setBulkProgress({ done, total: clean.length, errors });

      // After successful send, stamp TO-matching siblings.
      if (sendOk) {
        const ref = req.extracted_reference_number || req.request_id;
        const siblings = siblingsByRef.get(ref) || [];
        const sentTo = toSetNorm(req.response_recipients?.to);
        const now = new Date().toISOString();

        for (const sib of siblings) {
          const sibDetail = detailMap.get(sib.request_id);
          const sibTo = toSetNorm(sibDetail?.response_recipients?.to);

          if (!setsEqual(sentTo, sibTo)) {
            conflictRefs.push({
              ref,
              sibling_id: sib.request_id,
              sent_to: Array.from(sentTo),
              sibling_to: Array.from(sibTo),
            });
            continue;
          }

          try {
            const existingNotes = sibDetail?.reviewer_notes || '';
            const stampLine = `bulk_dedup_sibling | dup_of=${req.request_id} | ref=${ref} | stamped=${now}`;
            const newNotes = existingNotes ? `${existingNotes}\n${stampLine}` : stampLine;
            await api.updateConvexRequest(sib.request_id, { reviewer_notes: newNotes });
            await api.archiveConvexRequest(sib.request_id);
            stampedSiblings.push(sib.request_id);
          } catch (e: any) {
            stampFailures.push({ request_id: sib.request_id, error: e?.message || 'unknown' });
          }
        }
      }

      // Pacing: throttle to avoid EOP AS(42004) burst detection
      if (done < clean.length && !bulkCancelRef.current) {
        await new Promise(resolve => setTimeout(resolve, BULK_APPROVE_DELAY_MS));
      }
    }

    setBulkApproving(false);

    // Post-run summary. Amos-69: repeats are sent in the pass with their dup
    // evidence annotated — the summary reports the annotation count, not a
    // hold count.
    const sentCount = done - errors;
    const parts: string[] = [`Bulk approve complete: ${sentCount} sent${duplicateHoldCount > 0 ? ` (${duplicateHoldCount} repeat(s) annotated)` : ''}.`];
    if (errors > 0) {
      parts.push(`${errors} error(s) during send.`);
    }
    if (duplicateHoldFailCount > 0) {
      parts.push(`${duplicateHoldFailCount} dup-annotation write(s) failed — the record(s) still sent; note the prior-send evidence manually.`);
    }
    if (stampedSiblings.length > 0) {
      parts.push(`${stampedSiblings.length} duplicate sibling(s) auto-archived.`);
    }
    if (stampFailures.length > 0) {
      parts.push(`${stampFailures.length} sibling stamp(s) failed — left eligible for manual review.`);
    }
    if (conflictRefs.length > 0) {
      const uniqueRefs = Array.from(new Set(conflictRefs.map(c => c.ref)));
      parts.push(`Distinct broker contacts on ${uniqueRefs.length} ref(s) — needs its own send: ${uniqueRefs.join(', ')}`);
    }
    window.alert(parts.join('\n'));

    await fetchRequests();
    const d = await api.getConvexRequests();
    setAllRequests(d.requests || []);
    api.getConvexRequests({ triage_class: 'D' }).then((d: any) => setDCount(d.count || 0)).catch(() => {});
  };

  const handlePoll = async () => {
    setPolling(true);
    try {
      await api.pollConvexMailbox();
      await fetchRequests();
      const d = await api.getConvexRequests();
      setAllRequests(d.requests || []);
    } catch (err: any) {
      setError(err.message || 'Poll failed');
    } finally {
      setPolling(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.updateConvexRequest(deleteTarget.requestId, { action: 'soft_delete' });
      setDeleteTarget(null);
      await fetchRequests();
      api.getConvexRequests({ triage_class: 'D' }).then((d: any) => setDCount(d.count || 0)).catch(() => {});
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const viewingDTab = activeTab === 'D';

  const attemptRows: AttemptRow[] = viewingDTab ? (() => {
    const rows: AttemptRow[] = [];
    const groupMaxTimes = new Map<string, string>();

    for (const req of requests) {
      const attempts = getAttemptsView(req);
      const count = Math.max(attempts.length, 1);
      for (const att of attempts) {
        rows.push({ request: req, attempt: att, attemptCount: count, groupSortKey: '' });
        const prev = groupMaxTimes.get(req.request_id) || '';
        if (att.attempted_at > prev) groupMaxTimes.set(req.request_id, att.attempted_at);
      }
      if (attempts.length === 0) {
        const fallback: SendAttempt = {
          attempt_id: req.request_id,
          attempt_n: 1,
          attempted_at: req.approved_at || req.received_at || '',
          send_result: req.send_result || {},
          delivery_status: req.delivery_status || 'unknown',
          delivery_failures: req.delivery_failures || [],
          recipients: req.response_recipients || {},
        };
        rows.push({ request: req, attempt: fallback, attemptCount: 1, groupSortKey: '' });
        groupMaxTimes.set(req.request_id, fallback.attempted_at);
      }
    }

    for (const row of rows) {
      row.groupSortKey = groupMaxTimes.get(row.request.request_id) || '';
    }

    rows.sort((a, b) => {
      if (a.groupSortKey !== b.groupSortKey) return b.groupSortKey.localeCompare(a.groupSortKey);
      if (a.request.request_id !== b.request.request_id) return a.request.request_id.localeCompare(b.request.request_id);
      return a.attempt.attempt_n - b.attempt.attempt_n;
    });

    return rows;
  })() : [];

  return (
    <div onClick={() => { if (highlightedRequestId) setHighlightedRequestId(null); }}>
      {/* Header row with poll button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setLoading(true); setShowZeroClaimsOnly(false); setSearchParams(tab.key === 'all' ? {} : { tab: tab.key }); }}
              className={[
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                activeTab === tab.key
                  ? 'bg-blue-600 text-white'
                  : `bg-white border border-slate-200 ${tab.color || 'text-slate-700'} hover:bg-slate-50`,
              ].join(' ')}
            >
              {tab.label}
              {countFor(tab.key) > 0 && (
                <span className={`ml-1.5 ${activeTab === tab.key ? 'text-blue-200' : 'text-slate-400'}`}>
                  {countFor(tab.key)}
                </span>
              )}
            </button>
          ))}
        </div>
        <button
          onClick={handlePoll}
          disabled={polling}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 transition-colors"
        >
          <svg className={`w-4 h-4 ${polling ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {polling ? 'Polling...' : 'Check Mailbox'}
        </button>
      </div>

      {/* Queue search bar */}
      <div className="mb-4 flex items-center gap-4">
        <input
          type="text"
          value={queueSearch}
          onChange={(e) => setQueueSearch(e.target.value)}
          placeholder="Search by reference number or insured name..."
          className="w-full max-w-md px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {reviewFilterActive && (
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden whitespace-nowrap" title="Reviewed records are hidden in the default view — All shows everything">
            <button
              onClick={() => setReviewFilter('new')}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                reviewFilter === 'new' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              New + Changed ({tabRows.length - reviewedCount})
            </button>
            <button
              onClick={() => setReviewFilter('all')}
              className={`px-3 py-2 text-sm font-medium border-l border-slate-300 transition-colors ${
                reviewFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              All ({tabRows.length})
            </button>
          </div>
        )}
        {activeTab === 'C' && gateEligibleCount > 0 && (
          <button
            onClick={() => setShowZeroClaimsOnly(!showZeroClaimsOnly)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border transition-colors whitespace-nowrap ${
              showZeroClaimsOnly
                ? 'bg-green-100 text-green-700 border-green-300'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Gate Eligible ({gateEligibleCount})
          </button>
        )}
        {viewingDTab && (
          <button
            onClick={() => { setShowDeleted(!showDeleted); setLoading(true); }}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border transition-colors whitespace-nowrap ${
              showDeleted
                ? 'bg-red-100 text-red-700 border-red-300'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Show Deleted
          </button>
        )}
      </div>

      <UnmatchedFeedbackBucket entries={unmatchedFeedback} />

      {activeTab === 'C' && gateEligibleCount > 0 && !bulkApproving && !bulkResolving && isBulkApproveAuthorized && (
        <div className="mb-4 flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <span className="text-sm text-green-700">
            <span className="font-medium">{gateEligibleCount}</span> gate-eligible request{gateEligibleCount !== 1 ? 's' : ''} ready for bulk approve
          </span>
          <button
            onClick={() => handleBulkApprove()}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
          >
            Bulk Approve
          </button>
        </div>
      )}
      {bulkResolving && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-700">
              Resolving recipients... {bulkResolveProgress.done}/{bulkResolveProgress.total}
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${bulkResolveProgress.total > 0 ? (bulkResolveProgress.done / bulkResolveProgress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}
      {bulkExcluded.length > 0 && !bulkApproving && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <p className="text-sm font-medium text-amber-800 mb-1">{bulkExcluded.length} record(s) excluded from bulk — recipient issues detected</p>
          <p className="text-xs text-amber-600">These need manual review via single approve (sender-fallback, requester-echo, or empty recipients).</p>
        </div>
      )}
      {bulkApproving && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-700">
              Approving... {bulkProgress.done}/{bulkProgress.total}
              {bulkProgress.errors > 0 && <span className="text-red-600 ml-2">({bulkProgress.errors} errors)</span>}
            </span>
            <button
              onClick={() => { bulkCancelRef.current = true; }}
              className="text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Cancel
            </button>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${bulkProgress.total > 0 ? (bulkProgress.done / bulkProgress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-lg shadow border border-slate-200 p-12 text-center">
          <svg className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <p className="text-sm text-slate-500">Loading requests...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && requests.length === 0 && (
        <div className="bg-white rounded-lg shadow border border-slate-200 p-8 text-center text-slate-500 text-sm">
          {queueSearch
            ? 'No requests match your search.'
            : activeTab === 'all'
            ? 'No requests yet. Upload a master file and use "Check Mailbox" to poll for new requests.'
            : activeTab === 'D'
            ? 'No completed requests yet.'
            : `No class ${activeTab} requests.`}
        </div>
      )}

      {/* Request cards — D-tab uses row-per-attempt; other tabs use row-per-request */}
      {!loading && requests.length > 0 && viewingDTab && (
        <div className="space-y-1">
          {attemptRows.map((row) => {
            const { request: req, attempt: att, attemptCount } = row;
            const isDeleted = !!req.deleted_manually;
            const hue = requestIdToHue(req.request_id);
            const isHighlighted = highlightedRequestId === req.request_id;
            const isDimmed = highlightedRequestId !== null && !isHighlighted;

            return (
              <div
                key={`${req.request_id}-${att.attempt_n}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (highlightedRequestId === req.request_id) {
                    setHighlightedRequestId(null);
                    navigate(`/convex/request/${req.request_id}?tab=D`);
                  } else {
                    setHighlightedRequestId(req.request_id);
                  }
                }}
                className={[
                  'w-full text-left rounded-lg shadow-sm border border-slate-200 hover:border-blue-300 hover:shadow transition-all cursor-pointer flex',
                  isDeleted ? 'opacity-50' : '',
                  isDimmed ? 'opacity-30' : '',
                  isHighlighted ? 'ring-2 ring-blue-400' : '',
                ].filter(Boolean).join(' ')}
                style={{ borderLeftWidth: '4px', borderLeftColor: `hsl(${hue}, 60%, 70%)` }}
              >
                <div className="flex-1 px-5 py-3">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <InsurerBadge insurer={req.insurer} name={req.insurer_name} />
                    <StatusBadge status={req.status} />
                    <AttemptDeliveryBadge attempt={att} />
                    <AttemptChip n={att.attempt_n} total={attemptCount} />
                    {isDeleted && (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        Deleted
                      </span>
                    )}
                    <span className="text-xs text-slate-400">{att.attempted_at ? relativeTime(att.attempted_at) : relativeTime(req.received_at)}</span>
                  </div>
                  <p className={`text-sm font-semibold text-slate-800 truncate ${isDeleted ? 'line-through' : ''}`}>
                    {req.extracted_insured_name || 'Unknown -- see email'}
                  </p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                    <span>
                      <span className="font-medium text-slate-600">Ref:</span>{' '}
                      {req.extracted_reference_number || 'Not extracted'}
                    </span>
                    <span className="truncate" title={req.email_from}>
                      From: {req.email_from}
                    </span>
                  </div>
                  {isDeleted && req.deleted_at && (
                    <div className="flex items-center gap-3 mt-1 text-xs text-red-400">
                      <span>Deleted {formatDate(req.deleted_at)}</span>
                      {req.deleted_by && <span>by {req.deleted_by}</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 pr-4 flex-shrink-0">
                  {!isDeleted && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget({ requestId: req.request_id, attemptCount });
                      }}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      title="Delete request"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                  <svg className="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && requests.length > 0 && !viewingDTab && (
        <div className="space-y-2">
          {requests.filter(req => {
            if (['A', 'B', 'C'].includes(activeTab) && req.triage_class !== activeTab) return false;
            if (showZeroClaimsOnly && !(req.status === 'parsed' && isGateEligible(req, w1cMap[req.request_id] ?? 'pending'))) return false;
            // Amos-64 default view: hide Reviewed on B/C unless All is selected
            if (reviewFilterActive && reviewFilter === 'new' && reviewVerdict(req).state === 'reviewed') return false;
            return true;
          }).map((req) => (
            <button
              key={req.request_id}
              onClick={() => navigate(buildRequestUrl(req.request_id, activeTab, reviewFilterActive ? reviewFilter : undefined))}
              className="w-full text-left bg-white rounded-lg shadow-sm border border-slate-200 hover:border-blue-300 hover:shadow transition-all px-5 py-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <InsurerBadge insurer={req.insurer} name={req.insurer_name} />
                    <TriageBadge triage={req.triage_class} />
                    <StatusBadge status={req.status} />
                    <DeliveryBadge request={req} />
                    <QueueTypeBadge queueType={req.queue_type} />
                    <GateEligibilityBadge req={req} w1c={w1cMap[req.request_id] ?? 'pending'} />
                    <ReviewBadge req={req} />
                    <FeedbackChip entries={feedbackByRequest[req.request_id]} />
                    <span className="text-xs text-slate-400">{relativeTime(req.received_at)}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-800 truncate">
                    {req.extracted_insured_name || 'Unknown -- see email'}
                  </p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                    <span>
                      <span className="font-medium text-slate-600">Ref:</span>{' '}
                      {req.extracted_reference_number || 'Not extracted'}
                    </span>
                    <span className="truncate" title={req.email_from}>
                      From: {req.email_from}
                    </span>
                  </div>
                  {req.email_subject && (
                    <p className="text-xs text-slate-400 mt-1 truncate">{req.email_subject}</p>
                  )}
                </div>
                <svg className="w-5 h-5 text-slate-300 flex-shrink-0 mt-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={(e) => { e.stopPropagation(); setDeleteTarget(null); }}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-800 mb-2">Delete this request?</h3>
            <p className="text-sm text-slate-600 mb-4">
              {deleteTarget.attemptCount > 1
                ? `This will hide the request and all ${deleteTarget.attemptCount} attempts from default views. It can be recovered by an administrator.`
                : 'This will hide the request from default views. It can be recovered by an administrator.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 rounded-lg transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  REQUEST DETAIL
// ═══════════════════════════════════════════════════════════════════════════════

function RequestDetail() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromTab = searchParams.get('tab') || 'all';
  const backToQueueUrl = buildQueueUrl(fromTab, searchParams.get('filter'));
  const [request, setRequest] = useState<ConvexRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sendAck, setSendAck] = useState(false);
  const [sendMode, setSendMode] = useState<string>('off');

  // Editable fields for B items
  const [editRef, setEditRef] = useState('');
  const [editInsured, setEditInsured] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // Recipient correction state
  const [editTo, setEditTo] = useState<string[]>([]);
  const [editCc, setEditCc] = useState<string[]>([]);
  const [newToInput, setNewToInput] = useState('');
  const [newCcInput, setNewCcInput] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [recipientSaving, setRecipientSaving] = useState(false);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [showEmailBody, setShowEmailBody] = useState(true);
  const [highlightEmails, setHighlightEmails] = useState(true);
  const recipientsDirtyRef = useRef(false);

  const [signerName, setSignerName] = useState<string>('');
  useEffect(() => {
    fetchAuthSession()
      .then((session) => {
        const payload = session.tokens?.idToken?.payload;
        const name = payload?.['name'] as string | undefined;
        const given = payload?.['given_name'] as string | undefined;
        const family = payload?.['family_name'] as string | undefined;
        if (name) setSignerName(name);
        else if (given && family) setSignerName(`${given} ${family}`);
      })
      .catch(() => {});
  }, []);

  const fetchRequest = useCallback(async () => {
    if (!requestId) return;
    setError(null);
    try {
      const data = await api.getConvexRequest(requestId);
      setRequest(data);
      setEditRef(data.extracted_reference_number || '');
      setEditInsured(data.extracted_insured_name || '');
      setEditNotes(data.reviewer_notes || '');
      if (!recipientsDirtyRef.current) {
        setEditTo((data.response_recipients?.to || []).map((r: any) => r.email));
        setEditCc((data.response_recipients?.cc || []).map((r: any) => r.email));
        setNewToInput('');
        setNewCcInput('');
        setCorrectionReason('');
      }
      setRecipientError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => { fetchRequest(); }, [fetchRequest]);
  useEffect(() => { api.getConvexSendMode().then(setSendMode).catch(() => {}); }, []);

  // Amos-64: feedback entries bound to this record (page_url/request_id join)
  const [detailFeedback, setDetailFeedback] = useState<FeedbackEntry[]>([]);
  const refreshFeedback = useCallback(() => {
    if (!requestId) return;
    api.getConvexFeedback()
      .then((d: any) => {
        const bound = bindFeedback((d.items || []) as FeedbackEntry[]);
        setDetailFeedback(bound.byRequest[requestId.toLowerCase()] || bound.byRequest[requestId] || []);
      })
      .catch(() => {});
  }, [requestId]);
  useEffect(() => { refreshFeedback(); }, [refreshFeedback]);

  const hasRecipientDiff = (() => {
    if (!request?.response_recipients) return false;
    const committedTo = request.response_recipients.to.map(r => r.email);
    const committedCc = request.response_recipients.cc.map(r => r.email);
    return JSON.stringify([...editTo].sort()) !== JSON.stringify([...committedTo].sort())
      || JSON.stringify([...editCc].sort()) !== JSON.stringify([...committedCc].sort());
  })();

  const handleSave = async () => {
    if (!requestId) return;
    if (hasRecipientDiff) {
      setRecipientError('Uncommitted recipient changes — use "Save and re-verify" in the Recipients panel first.');
      document.getElementById('recipients-panel')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    setSaving(true);
    try {
      await api.updateConvexRequest(requestId, {
        extracted_reference_number: editRef,
        extracted_insured_name: editInsured,
        reviewer_notes: editNotes,
      });
      await fetchRequest();
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePromote = async () => {
    if (!requestId) return;
    if (hasRecipientDiff) {
      setRecipientError('Uncommitted recipient changes — use "Save and re-verify" in the Recipients panel first.');
      document.getElementById('recipients-panel')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    setSaving(true);
    try {
      await api.updateConvexRequest(requestId, {
        extracted_reference_number: editRef,
        extracted_insured_name: editInsured,
        reviewer_notes: editNotes,
        triage_class: 'C',
      });
      await fetchRequest();
    } catch (err: any) {
      setError(err.message || 'Promote failed');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!requestId) return;
    if (hasRecipientDiff) {
      setShowConfirm(false);
      setRecipientError('Uncommitted recipient changes — use "Save corrections" in the Recipients panel first.');
      document.getElementById('recipients-panel')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    setApproving(true);
    setShowConfirm(false);
    try {
      const result = await api.approveConvexRequest(requestId);
      setRequest((prev) => prev ? { ...prev, ...result } : prev);
      await autoStamp(requestId, 'approve');
      await fetchRequest();
    } catch (err: any) {
      setError(err.message || 'Approve failed');
    } finally {
      setApproving(false);
    }
  };

  // Amos-69: "Send anyway" is gone — the plain Approve button serves dup
  // records now that the tag is informational. "Archive as duplicate" stays
  // as an explicit human action.
  const [archivingDuplicate, setArchivingDuplicate] = useState(false);
  const handleArchiveAsDuplicate = async () => {
    if (!requestId) return;
    if (!window.confirm('Archive this request as a confirmed duplicate? It will move to the Completed bucket.')) return;
    setArchivingDuplicate(true);
    try {
      await api.archiveConvexRequest(requestId, { reason: 'duplicate_of_prior_send' });
      const refreshed = await api.getConvexRequest(requestId);
      // A pre-A7 Lambda accepts the archive but silently records
      // archived_reason='manual' and drops the reason — detect and surface it
      // so the duplicate classification isn't lost without anyone knowing.
      if (refreshed?.archived_reason && refreshed.archived_reason !== 'duplicate_of_prior_send') {
        setError(`Archived, but the backend recorded reason "${refreshed.archived_reason}" instead of "duplicate_of_prior_send" — Lambda predates the duplicate-hold feature. Note the duplicate classification manually.`);
      }
      await autoStamp(requestId, 'archive_duplicate');
      await fetchRequest();
    } catch (err: any) {
      setError(err.message || 'Archive failed');
    } finally {
      setArchivingDuplicate(false);
    }
  };

  const [detailDeleting, setDetailDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const handleDetailDelete = async () => {
    if (!requestId) return;
    setDetailDeleting(true);
    setShowDeleteConfirm(false);
    try {
      await api.updateConvexRequest(requestId, { action: 'soft_delete' });
      navigate(backToQueueUrl);
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    } finally {
      setDetailDeleting(false);
    }
  };

  const [archiving, setArchiving] = useState(false);
  const handleArchive = async () => {
    if (!requestId) return;
    setArchiving(true);
    try {
      await api.archiveConvexRequest(requestId);
      await autoStamp(requestId, 'archive');
      await fetchRequest();
    } catch (err: any) {
      setError(err.message || 'Archive failed');
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      </div>
    );
  }

  if (error && !request) {
    return (
      <div className="bg-white rounded-lg shadow border border-red-200 p-8 text-center">
        <p className="text-red-600 font-medium mb-2">{error}</p>
        <button onClick={() => navigate(backToQueueUrl)} className="text-sm text-blue-600 hover:underline">
          Back to Queue
        </button>
      </div>
    );
  }

  if (!request) return null;

  const policyData = typeof request.policy_data === 'string' ? JSON.parse(request.policy_data) : request.policy_data;
  const registryPreview = request.registry_preview && typeof request.registry_preview === 'object' ? request.registry_preview : null;
  const usingRegistryPreview = !policyData && !!registryPreview;
  const claimData = typeof request.fh_claim_data === 'string' ? JSON.parse(request.fh_claim_data) : request.fh_claim_data;
  const claims = Array.isArray(claimData?.claims) ? claimData.claims : [];

  const templateFields = [
    { label: 'Insured Name', value: policyData?.insured_name || registryPreview?.insured_name || request.extracted_insured_name, key: 'insured' },
    { label: 'Reference Number', value: request.extracted_reference_number, key: 'ref' },
    { label: 'Policy Number', value: policyData?.p1_term || policyData?.reference_number || registryPreview?.reference_number, key: 'policy' },
    { label: 'Effective Date', value: policyData?.p1_effective || registryPreview?.effective_date, key: 'effective' },
    { label: 'Expiry Date', value: policyData?.p1_expiration || registryPreview?.expiration_date, key: 'expiry' },
    { label: 'Named Insured', value: policyData?.insured_name || registryPreview?.insured_name, key: 'named' },
    { label: 'LOB / Product', value: policyData?.product_code || registryPreview?.product_code, key: 'lob' },
    { label: 'Claims Found', value: claims.length > 0 ? `${claims.length} claim(s)` : 'None', key: 'claims' },
  ];

  const isArchived = request.triage_class === 'D';
  const isEditable = !isArchived && (request.triage_class === 'B' || request.triage_class === 'A');
  const canEditRecipients = !isArchived && request.status !== 'sent' && request.status !== 'approved';
  // Amos-69: duplicate_hold no longer withholds the plain Approve button —
  // the dup tag is informational (see the Repeat panel below). Every request
  // gets a response (standing rule 08-04).
  const canApprove = !isArchived && request.status !== 'sent' && request.status !== 'approved' && request.triage_class === 'C';
  const alreadyApproved = request.status === 'approved' || request.status === 'sent';
  const canArchive = !isArchived && (alreadyApproved || request.triage_class === 'C');

  return (
    <div>
      {/* Back button + header */}
      <button
        onClick={() => navigate(backToQueueUrl)}
        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium mb-4"
      >
        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
        </svg>
        Back to Queue
      </button>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <h2 className="text-xl font-bold text-slate-800">
          {policyData?.insured_name || registryPreview?.insured_name || request.extracted_insured_name || 'Unknown Insured'}
        </h2>
        <InsurerBadge insurer={request.insurer} name={request.insurer_name} />
        <TriageBadge triage={request.triage_class} />
        <StatusBadge status={request.status} />
        <QueueTypeBadge queueType={request.queue_type} />
        <GateEligibilityBadge req={request} />
      </div>
      {request.triage_class === 'C' && isGateEligible(request) && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm bg-green-50 border border-green-200 text-green-700">
          <span className="font-medium">Gate Ready</span> — verified zero claims, recipients confirmed, ready to send.
          {request.auto_verified_at && <span className="ml-1 text-green-500">Verified {formatDate(request.auto_verified_at)}</span>}
        </div>
      )}
      {request.triage_class === 'C' && !isGateEligible(request) && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm bg-amber-50 border border-amber-200 text-amber-700">
          <span className="font-medium">Held</span> — {gateHoldReasons(request).join(', ') || 'pending checks'}.
        </div>
      )}
      {request.auto_verified === 'zero_claims' && request.triage_class !== 'C' && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm bg-teal-50 border border-teal-200 text-teal-700">
          <span className="font-medium">Auto-verified: zero claims</span> — policy found, no claims on file.
          {request.auto_verified_at && <span className="ml-1 text-teal-500">Verified {formatDate(request.auto_verified_at)}</span>}
        </div>
      )}
      {request.request_state === 'complete' && !request.auto_verified && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm bg-green-50 border border-green-200 text-green-700">
          <span className="font-medium">Document complete</span> — ready for review
        </div>
      )}
      {request.request_state === 'no_record' && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm bg-blue-50 border border-blue-200 text-blue-700">
          <span className="font-medium">No current record found</span> — no action required
        </div>
      )}
      {request.request_state === 'needs_review' && request.triage_reason && (
        <div className={`mb-4 rounded-lg px-4 py-3 text-sm ${
          request.triage_class === 'A' ? 'bg-red-50 border border-red-200 text-red-700' :
          'bg-amber-50 border border-amber-200 text-amber-700'
        }`}>
          <span className="font-medium">Triage Reason:</span> {request.triage_reason}
        </div>
      )}
      {!request.request_state && request.triage_reason && (
        <div className={`mb-4 rounded-lg px-4 py-3 text-sm ${
          request.triage_class === 'A' ? 'bg-red-50 border border-red-200 text-red-700' :
          request.triage_class === 'B' ? 'bg-amber-50 border border-amber-200 text-amber-700' :
          'bg-green-50 border border-green-200 text-green-700'
        }`}>
          <span className="font-medium">Triage Reason:</span> {request.triage_reason}
        </div>
      )}

      <WhatChangedPanel req={request} onStamped={fetchRequest} />
      <FeedbackSection entries={detailFeedback} onResolved={refreshFeedback} />

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Left: Original email */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Original Email</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs font-medium text-slate-500 uppercase">Subject</dt>
              <dd className="text-sm text-slate-800 mt-0.5">{request.email_subject || '--'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500 uppercase">From</dt>
              <dd className="text-sm text-slate-800 mt-0.5">{request.email_from || '--'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500 uppercase">Received</dt>
              <dd className="text-sm text-slate-800 mt-0.5">
                {formatDate(request.received_at)} {formatTime(request.received_at)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500 uppercase">Body</dt>
              <dd className="text-sm text-slate-700 mt-1 whitespace-pre-wrap bg-slate-50 rounded p-3 max-h-60 overflow-y-auto border border-slate-100">
                {request.email_body || '(email body not stored)'}
              </dd>
            </div>
            {request.attachment_s3_keys && request.attachment_s3_keys.length > 0 && (
              <div>
                <dt className="text-xs font-medium text-slate-500 uppercase">Attachments</dt>
                <dd className="mt-1 space-y-1">
                  {request.attachment_s3_keys.map((key, i) => (
                    <div key={i} className="text-xs text-blue-600 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      {key.split('/').pop()}
                    </div>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Right: Template preview */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Template Preview</h3>
            {usingRegistryPreview && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 normal-case">
                from policy registry
              </span>
            )}
          </div>
          <div className="space-y-2">
            {templateFields.map((field) => {
              const missing = !field.value || field.value === 'None';
              return (
                <div
                  key={field.key}
                  className={`flex items-center justify-between px-3 py-2 rounded text-sm ${
                    missing ? 'bg-yellow-50 border border-yellow-200' : 'bg-slate-50 border border-slate-100'
                  }`}
                >
                  <span className={`font-medium ${missing ? 'text-yellow-700' : 'text-slate-600'}`}>
                    {field.label}
                  </span>
                  <span className={missing ? 'text-yellow-600 italic' : 'text-slate-800'}>
                    {field.value || 'Missing'}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Claims table if found */}
          {claims.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-slate-600 uppercase mb-2">Claims ({claims.length})</h4>
              <div className="overflow-x-auto rounded border border-slate-200">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="px-3 py-1.5 text-left font-medium text-slate-500">Claim #</th>
                      <th className="px-3 py-1.5 text-left font-medium text-slate-500">Status</th>
                      <th className="px-3 py-1.5 text-left font-medium text-slate-500">Loss Date</th>
                      <th className="px-3 py-1.5 text-right font-medium text-slate-500">Incurred</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {claims.slice(0, 10).map((c: any, i: number) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 text-slate-700 font-medium">{c.claim_number || c.fh_claim_num || '--'}</td>
                        <td className="px-3 py-1.5 text-slate-600">{c.status || c.open_close_status || '--'}</td>
                        <td className="px-3 py-1.5 text-slate-600">{formatDate(c.loss_date)}</td>
                        <td className="px-3 py-1.5 text-right text-slate-700">
                          {c.total_incurred != null ? `$${Number(c.total_incurred).toLocaleString()}` : '--'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {claims.length > 10 && (
                  <div className="px-3 py-1.5 text-xs text-slate-400 bg-slate-50 border-t">
                    + {claims.length - 10} more claims
                  </div>
                )}
              </div>
            </div>
          )}

          {claims.length === 0 && request.request_state === 'no_record' && (
            <div className="mt-4 px-3 py-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
              We have no record matching this reference. Please verify the reference number and resend if needed.
            </div>
          )}

          {/* Download link */}
          {request.template_download_url && (
            <a
              href={request.template_download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {request.download_is_pdf ? 'Download PDF' : 'Download Word Doc'}
            </a>
          )}
        </div>
      </div>

      {/* AI Extraction Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">AI Extraction Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <span className="text-xs font-medium text-slate-500 uppercase">Insurer</span>
            <p className="text-sm text-slate-800 mt-0.5">
              {request.insurer_name || request.insurer || 'Not classified'}
            </p>
          </div>
          <div>
            <span className="text-xs font-medium text-slate-500 uppercase">Reference Number</span>
            <p className="text-sm text-slate-800 mt-0.5">{request.extracted_reference_number || 'Not extracted'}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-slate-500 uppercase">Insured Name</span>
            <p className="text-sm text-slate-800 mt-0.5">{request.extracted_insured_name || 'Not extracted'}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-slate-500 uppercase">Confidence</span>
            <p className="text-sm mt-0.5">
              {(request.extraction_confidence || request.classifier_confidence) ? (
                <span className={`font-medium ${
                  (request.extraction_confidence || request.classifier_confidence) === 'high' ? 'text-green-600' :
                  (request.extraction_confidence || request.classifier_confidence) === 'medium' ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {request.extraction_confidence || request.classifier_confidence}
                </span>
              ) : (
                <span className="text-slate-400">--</span>
              )}
            </p>
          </div>
        </div>
        {(request.extraction_notes || request.classifier_reasoning) && (
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
            <span className="font-medium">AI Notes:</span> {request.classifier_reasoning || request.extraction_notes}
          </div>
        )}
        {request.parse_error && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            <span className="font-medium">Parse Error:</span> {request.parse_error}
          </div>
        )}
      </div>

      {/* ── Recipients Panel ── */}
      {request.response_recipients && (() => {
        const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const originalTo = request.response_recipients.to.map(r => r.email);
        const originalCc = request.response_recipients.cc.map(r => r.email);
        const isSenderFallback = request.response_recipients.extraction_method === 'sender_fallback';
        const isEmpty = isSenderFallback || request.response_recipients.to.length === 0;
        const hasDiff = JSON.stringify([...editTo].sort()) !== JSON.stringify([...originalTo].sort())
          || JSON.stringify([...editCc].sort()) !== JSON.stringify([...originalCc].sort());
        const senderDomain = request.email_from?.split('@')[1]?.toLowerCase();
        const CONVEX_SENDER_DOMAINS = new Set(['convexin.com', 'convex.com']);
        const isConvexinSender = !!(senderDomain && CONVEX_SENDER_DOMAINS.has(senderDomain));
        const senderIsValidRecipient = !!(request.email_from && !isConvexinSender && EMAIL_RE.test(request.email_from));
        const hasExternalTo = editTo.some(e => e.split('@')[1]?.toLowerCase() !== senderDomain);
        const echoDomains = [...editTo, ...(hasExternalTo ? [] : editCc)]
          .map(e => e.split('@')[1]?.toLowerCase())
          .filter(d => d === senderDomain);
        const confidence = request.extraction_confidence || request.classifier_confidence || 'low';
        const confDot = confidence === 'high' ? 'bg-green-500' : confidence === 'medium' ? 'bg-amber-500' : 'bg-red-500';

        const addEmail = (list: string[], setList: (v: string[]) => void, input: string, setInput: (v: string) => void) => {
          const email = input.trim().toLowerCase();
          if (!email) return;
          if (!EMAIL_RE.test(email)) { setRecipientError(`Invalid email: ${email}`); return; }
          if ([...editTo, ...editCc].includes(email)) { setRecipientError(`Duplicate: ${email}`); return; }
          setList([...list, email]);
          setInput('');
          setRecipientError(null);
          recipientsDirtyRef.current = true;
        };

        const removeEmail = (list: string[], setList: (v: string[]) => void, idx: number) => {
          setList(list.filter((_, i) => i !== idx));
          setRecipientError(null);
          recipientsDirtyRef.current = true;
        };

        const handleRecipientSave = async (isVerifyOnly: boolean) => {
          setRecipientError(null);

          // Auto-commit any typed-but-not-chipped text from inputs
          let effectiveTo = [...editTo];
          let effectiveCc = [...editCc];
          const pendingTo = newToInput.trim().toLowerCase();
          const pendingCc = newCcInput.trim().toLowerCase();
          if (pendingTo) {
            if (!EMAIL_RE.test(pendingTo)) { setRecipientError(`Invalid email: ${pendingTo}`); return; }
            if ([...effectiveTo, ...effectiveCc].includes(pendingTo)) { setRecipientError(`Duplicate: ${pendingTo}`); return; }
            effectiveTo = [...effectiveTo, pendingTo];
            setEditTo(effectiveTo);
            setNewToInput('');
          }
          if (pendingCc) {
            if (!EMAIL_RE.test(pendingCc)) { setRecipientError(`Invalid email: ${pendingCc}`); return; }
            if ([...effectiveTo, ...effectiveCc].includes(pendingCc)) { setRecipientError(`Duplicate: ${pendingCc}`); return; }
            effectiveCc = [...effectiveCc, pendingCc];
            setEditCc(effectiveCc);
            setNewCcInput('');
          }

          if (effectiveTo.length === 0) { setRecipientError('At least one To address is required.'); return; }
          for (const e of [...effectiveTo, ...effectiveCc]) {
            if (!EMAIL_RE.test(e)) { setRecipientError(`Invalid email: ${e}`); return; }
          }

          const effectiveDiff = JSON.stringify([...effectiveTo].sort()) !== JSON.stringify([...originalTo].sort())
            || JSON.stringify([...effectiveCc].sort()) !== JSON.stringify([...originalCc].sort());
          if (effectiveDiff && !correctionReason.trim()) { setRecipientError('Reason is required when recipients are changed.'); return; }

          setRecipientSaving(true);
          try {
            await api.updateConvexRequest(requestId!, {
              action: 'correct_recipients',
              recipients_to: effectiveTo,
              recipients_cc: effectiveCc,
              reason: effectiveDiff ? correctionReason.trim() : undefined,
              verify_only: isVerifyOnly,
            });
            recipientsDirtyRef.current = false;
            await autoStamp(requestId!, isVerifyOnly ? 'verify_recipients' : 'correct_recipients');
            await fetchRequest();
          } catch (err: any) {
            setRecipientError(err.message || 'Recipient save failed');
          } finally {
            setRecipientSaving(false);
          }
        };

        const renderChip = (email: string, idx: number, list: string[], setList: (v: string[]) => void, originals: string[]) => {
          const isManual = !originals.includes(email);
          return (
            <span key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm bg-slate-100 border border-slate-200">
              {isManual ? (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-600 uppercase leading-none">manual</span>
              ) : (
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${confDot}`} title={`${confidence} confidence`} />
              )}
              <span className="text-slate-800">{email}</span>
              {canEditRecipients && (
                <button onClick={() => removeEmail(list, setList, idx)} className="text-slate-400 hover:text-red-500 ml-0.5" title="Remove">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </span>
          );
        };

        const renderAddInput = (input: string, setInput: (v: string) => void, list: string[], setList: (v: string[]) => void, placeholder: string) => (
          <div className="flex gap-2 mt-2">
            <input
              type="email"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(list, setList, input, setInput); } }}
              className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={placeholder}
            />
            <button
              onClick={() => addEmail(list, setList, input, setInput)}
              className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
            >Add</button>
          </div>
        );

        const bodyText = request.email_body || request.outbound_email?.body || '';
        const highlightedBody = highlightEmails
          ? bodyText.replace(/([^\s@]+@[^\s@]+\.[^\s@]+)/g, '<<HL>>$1<</HL>>')
          : bodyText;

        return (
          <div id="recipients-panel" className={`bg-white rounded-lg shadow-sm border p-6 mb-6 ${
            isSenderFallback ? 'border-amber-300' : hasDiff ? 'border-blue-300' : 'border-slate-200'
          }`}>
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
              {canEditRecipients ? 'Recipients — Verify or Correct' : 'Response Recipients'}
            </h3>

            {/* Sender line with convexin flag */}
            {request.email_from && (
              <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
                <span className="text-xs font-medium text-slate-500 uppercase">From:</span>
                <span className="text-slate-800">{request.email_from}</span>
                {isConvexinSender && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-600 uppercase leading-none">
                    convexin
                  </span>
                )}
              </div>
            )}

            {/* Sender-fallback banner */}
            {isSenderFallback && (
              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm text-amber-700 flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <span className="font-semibold">No broker address found — fell back to sender.</span>
                  <span className="block text-amber-600 mt-0.5">Verify the correct recipient before saving.</span>
                </div>
              </div>
            )}

            {/* Use sender as recipient — non-convexin only */}
            {canEditRecipients && isSenderFallback && senderIsValidRecipient && (
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 text-sm text-blue-700 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Sender <strong>{request.email_from}</strong> is a valid broker address.</span>
                </div>
                <button
                  onClick={() => {
                    setEditTo([request.email_from!]);
                    setCorrectionReason('Sender is the requesting broker — adopting as recipient');
                    recipientsDirtyRef.current = true;
                  }}
                  disabled={recipientSaving}
                  className="flex-shrink-0 px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 rounded-md transition-colors whitespace-nowrap"
                >
                  Use sender as recipient
                </button>
              </div>
            )}

            {/* Convexin sender — advisory, not blocking */}
            {canEditRecipients && isSenderFallback && isConvexinSender && (
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 text-sm text-blue-700 flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <span className="font-semibold">Convex/Convexin address detected.</span>
                  <span className="block text-blue-600 mt-0.5">Usually CC-only on broker requests — check the email body for the broker's address. If Convex is the requester (e.g. a renewal sweep), this is the correct recipient: confirm and promote.</span>
                </div>
              </div>
            )}

            {/* Empty state headline */}
            {isEmpty && canEditRecipients && !isSenderFallback && (
              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm text-amber-700">
                Recipient extraction did not resolve a To address.
              </div>
            )}

            {/* Echo-send warning */}
            {echoDomains.length > 0 && canEditRecipients && (
              <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2.5 text-sm text-yellow-700">
                Sending back to the requester's own domain ({senderDomain}) — is this intentional?
              </div>
            )}

            {/* To list */}
            <div className="mb-4">
              <span className="text-xs font-medium text-slate-500 uppercase">To</span>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {editTo.length > 0 ? (
                  editTo.map((email, i) => renderChip(email, i, editTo, setEditTo, originalTo))
                ) : (
                  <span className="text-sm text-amber-600 italic">No To recipients</span>
                )}
              </div>
              {canEditRecipients && renderAddInput(newToInput, setNewToInput, editTo, setEditTo, 'Add To address...')}
            </div>

            {/* CC list */}
            <div className="mb-4">
              <span className="text-xs font-medium text-slate-500 uppercase">CC</span>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {editCc.length > 0 ? (
                  editCc.map((email, i) => renderChip(email, i, editCc, setEditCc, originalCc))
                ) : (
                  !canEditRecipients && <span className="text-sm text-slate-400">None</span>
                )}
              </div>
              {canEditRecipients && renderAddInput(newCcInput, setNewCcInput, editCc, setEditCc, 'Add CC address...')}
            </div>

            {/* Extraction method tag */}
            {request.response_recipients.extraction_method && (
              <p className="text-xs text-slate-400 mb-4">
                Method: {request.response_recipients.extraction_method}
              </p>
            )}

            {/* Reason field — appears reactively when diff is present */}
            {canEditRecipients && hasDiff && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Reason for change <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={correctionReason}
                  onChange={e => setCorrectionReason(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Why are these recipients being changed?"
                />
              </div>
            )}

            {/* Error display */}
            {recipientError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-sm text-red-700">
                {recipientError}
              </div>
            )}

            {/* Action buttons */}
            {canEditRecipients && (
              <div className="flex gap-3 mb-4">
                {!hasDiff ? (
                  <button
                    onClick={() => handleRecipientSave(true)}
                    disabled={recipientSaving}
                    className="px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300 rounded-lg transition-colors"
                  >
                    {recipientSaving ? 'Verifying...' : request.triage_class === 'C' ? 'Confirm recipients' : 'Verify and promote'}
                  </button>
                ) : (
                  <button
                    onClick={() => handleRecipientSave(false)}
                    disabled={recipientSaving}
                    className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 rounded-lg transition-colors"
                  >
                    {recipientSaving ? 'Saving...' : 'Save corrections'}
                  </button>
                )}
              </div>
            )}

            {/* Collapsible email body */}
            {bodyText && (
              <div className="border-t border-slate-200 pt-4">
                <button
                  onClick={() => setShowEmailBody(!showEmailBody)}
                  className="flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-700 uppercase tracking-wide"
                >
                  <svg className={`w-4 h-4 transition-transform ${showEmailBody ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Original Email Body
                </button>
                {showEmailBody && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={highlightEmails}
                          onChange={e => setHighlightEmails(e.target.checked)}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        Highlight addresses
                      </label>
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-3 border border-slate-100 max-h-64 overflow-y-auto">
                      {highlightedBody.split(/<<HL>>|<<\/HL>>/).map((part, i) =>
                        i % 2 === 1 ? (
                          <span key={i} className="bg-yellow-200 text-yellow-900 px-0.5 rounded">{part}</span>
                        ) : (
                          <span key={i}>{part}</span>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Outbound Email Preview */}
      {request.outbound_email && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Outbound Email Preview</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs font-medium text-slate-500 uppercase">Subject</dt>
              <dd className="text-sm text-slate-800 mt-0.5 font-medium">{request.outbound_email.subject}</dd>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-xs font-medium text-slate-500 uppercase">To</dt>
                {request.outbound_email.to.length > 0 ? (
                  request.outbound_email.to.map((email, i) => (
                    <dd key={i} className="text-sm text-slate-800 mt-0.5">{email}</dd>
                  ))
                ) : (
                  <dd className="text-sm text-amber-600 mt-0.5 italic">Not detected</dd>
                )}
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500 uppercase">CC</dt>
                {request.outbound_email.cc.length > 0 ? (
                  request.outbound_email.cc.map((email, i) => (
                    <dd key={i} className="text-sm text-slate-800 mt-0.5">{email}</dd>
                  ))
                ) : (
                  <dd className="text-sm text-slate-400 mt-0.5">None</dd>
                )}
              </div>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500 uppercase">Body</dt>
              <dd className="text-sm text-slate-700 mt-1 whitespace-pre-wrap bg-slate-50 rounded p-3 border border-slate-100">
                {resolveSignerLine(request.outbound_email.body || '', signerName)}
              </dd>
            </div>
          </dl>
        </div>
      )}

      {/* Editable fields for A/B items */}
      {isEditable && !alreadyApproved && (
        <div className="bg-white rounded-lg shadow-sm border border-amber-200 p-6 mb-6">
          <h3 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-4">
            Manual Review Required
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Reference Number</label>
              <input
                type="text"
                value={editRef}
                onChange={(e) => setEditRef(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. PGL000073"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Insured Name</label>
              <input
                type="text"
                value={editInsured}
                onChange={(e) => setEditInsured(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Insured name"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-600 mb-1">Reviewer Notes</label>
            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Notes about this request..."
            />
          </div>
          {hasRecipientDiff && (
            <div className="flex items-start gap-2 mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>Uncommitted recipient changes &mdash; use <strong>"Save corrections"</strong> in the Recipients panel above first.</span>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving || hasRecipientDiff}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 rounded-lg transition-colors"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={handlePromote}
              disabled={saving || hasRecipientDiff}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300 rounded-lg transition-colors"
            >
              Promote to Ready (C)
            </button>
          </div>
        </div>
      )}

      {/* Repeat panel (Amos-69): informational — the dup tag no longer holds
          the record. Plain Approve serves it; "Archive as duplicate" remains
          available as an explicit human choice. */}
      {(request.duplicate_hold || request.duplicate_hold_evidence) && !isArchived && !alreadyApproved && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-start gap-2 mb-3">
            <svg className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-slate-700">
                Repeat — prior response{request.duplicate_hold_evidence?.prior_sent_at ? ` ${formatDate(request.duplicate_hold_evidence.prior_sent_at)}` : ''}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                This reference was previously answered. Informational only — approve and send as normal, or archive as a duplicate if a response is not warranted.
              </p>
            </div>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
            <dt className="font-medium text-slate-500">Prior sent</dt>
            <dd className="text-slate-700">
              {request.duplicate_hold_evidence?.prior_sent_at
                ? `${formatDate(request.duplicate_hold_evidence.prior_sent_at)} ${formatTime(request.duplicate_hold_evidence.prior_sent_at)}`
                : '--'}
            </dd>
            <dt className="font-medium text-slate-500">Message ID</dt>
            <dd className="text-slate-700 break-all">
              {request.duplicate_hold_evidence?.prior_internet_message_id
                || request.duplicate_hold_evidence?.prior_message_id
                || '--'}
            </dd>
            <dt className="font-medium text-slate-500">Recipient (To)</dt>
            <dd className="text-slate-700">
              {(request.duplicate_hold_evidence?.recipient_to || []).join(', ') || '--'}
            </dd>
            {request.duplicate_hold_evidence?.prior_request_id && (
              <>
                <dt className="font-medium text-slate-500">Prior Request</dt>
                <dd className="text-slate-700">{request.duplicate_hold_evidence.prior_request_id}</dd>
              </>
            )}
          </dl>
          <div className="flex gap-3">
            <button
              onClick={handleArchiveAsDuplicate}
              disabled={approving || archivingDuplicate}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-lg transition-colors"
            >
              {archivingDuplicate ? 'Archiving...' : 'Archive as duplicate'}
            </button>
          </div>
        </div>
      )}

      {/* Approve button for C items */}
      {canApprove && (
        <div className="bg-white rounded-lg shadow-sm border border-green-200 p-6 mb-6">
          {request.multi_ref_note && (
            <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm text-amber-700">
              <span className="font-medium">Multi-reference:</span> {request.multi_ref_note}
            </div>
          )}
          {hasRecipientDiff && (
            <div className="flex items-start gap-2 mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>Uncommitted recipient changes &mdash; use <strong>"Save corrections"</strong> in the Recipients panel above first.</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-green-700">
                {sendMode === 'manual' ? 'Ready to Send' : 'Ready for Approval'}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {sendMode === 'manual'
                  ? 'Approving will send the loss run to the recipient.'
                  : 'This request is fully populated and ready for approval. Send is currently disabled.'}
              </p>
            </div>
            <button
              onClick={() => { setSendAck(false); setShowConfirm(true); }}
              disabled={approving || hasRecipientDiff}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300 rounded-lg transition-colors"
            >
              {approving ? 'Processing...' : sendMode === 'manual' ? 'Approve & Send' : 'Approve'}
            </button>
          </div>
        </div>
      )}

      {/* Approved status */}
      {alreadyApproved && !isArchived && (() => {
        const deliveryState = getDeliveryState(request);
        const isFailed = deliveryState === 'failed';
        const borderColor = isFailed ? 'border-red-200' : 'border-green-200';
        const bgColor = isFailed ? 'bg-red-50' : 'bg-green-50';
        return (
          <div className={`${bgColor} border ${borderColor} rounded-lg px-6 py-4 mb-6`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isFailed ? (
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                <span className={`text-sm font-semibold ${isFailed ? 'text-red-700' : 'text-green-700'}`}>
                  {request.status === 'sent' ? 'Sent' : 'Approved'}
                  {request.approved_by && ` by ${request.approved_by}`}
                  {request.approved_at && ` on ${formatDate(request.approved_at)}`}
                </span>
                {deliveryState && <DeliveryBadge request={request} />}
              </div>
              <div className="flex items-center gap-2">
                {canArchive && (
                  <button
                    onClick={handleArchive}
                    disabled={archiving}
                    className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-md transition-colors"
                  >
                    {archiving ? 'Archiving...' : 'Archive to Completed'}
                  </button>
                )}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={detailDeleting}
                  className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 hover:bg-red-50 disabled:opacity-50 rounded-md transition-colors"
                >
                  {detailDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
            {request.send_result && !request.send_result.sent && (
              <p className="text-xs text-amber-600 mt-1">
                Send not executed: {request.send_result.reason || 'Send mode is off'}
              </p>
            )}
            {isFailed && request.delivery_failures && request.delivery_failures.length > 0 && (
              <DeliveryFailurePanel failures={request.delivery_failures} />
            )}
          </div>
        );
      })()}

      {/* Archived status */}
      {isArchived && (() => {
        const deliveryState = getDeliveryState(request);
        const isFailed = deliveryState === 'failed';
        return (
          <div className={`${isFailed ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'} border rounded-lg px-6 py-4 mb-6`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8" />
                </svg>
                <span className="text-sm font-semibold text-slate-700">Completed / Archived</span>
                {deliveryState && <DeliveryBadge request={request} />}
              </div>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={detailDeleting}
                className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 hover:bg-red-50 disabled:opacity-50 rounded-md transition-colors"
              >
                {detailDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-slate-600">
              {request.archived_at && (
                <div>
                  <span className="font-medium text-slate-500 uppercase">Archived</span>
                  <p className="mt-0.5">{formatDate(request.archived_at)} {formatTime(request.archived_at)}</p>
                </div>
              )}
              {request.archived_by && (
                <div>
                  <span className="font-medium text-slate-500 uppercase">By</span>
                  <p className="mt-0.5">{request.archived_by}</p>
                </div>
              )}
              {request.archived_reason && (
                <div>
                  <span className="font-medium text-slate-500 uppercase">Reason</span>
                  <p className="mt-0.5">{request.archived_reason === 'auto_sent' ? 'Automatically archived (sent)' : 'Manually archived'}</p>
                </div>
              )}
            </div>
            {request.archive_log && (
              <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500">
                <span className="font-medium">Previous state:</span>{' '}
                {request.archive_log.previous_triage_class && `Bucket ${request.archive_log.previous_triage_class}`}
                {request.archive_log.previous_status && ` / ${request.archive_log.previous_status}`}
              </div>
            )}
            {alreadyApproved && request.approved_at && (
              <div className="mt-2 text-xs text-slate-500">
                <span className="font-medium">{request.status === 'sent' ? 'Sent' : 'Approved'}:</span>{' '}
                {formatDate(request.approved_at)}
                {request.approved_by && ` by ${request.approved_by}`}
              </div>
            )}
            {isFailed && request.delivery_failures && request.delivery_failures.length > 0 && (
              <DeliveryFailurePanel failures={request.delivery_failures} />
            )}
          </div>
        );
      })()}

      {/* ── Send Attempts Timeline ── */}
      {(() => {
        const attempts = getAttemptsView(request);
        if (attempts.length === 0) return null;
        return (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">
              Send Attempts ({attempts.length})
            </h3>
            <div className="relative">
              {attempts.length > 1 && (
                <div className="absolute left-4 top-6 bottom-6 w-0.5 bg-slate-200" />
              )}
              <div className="space-y-4">
                {attempts.map((att, idx) => {
                  const state = getAttemptDeliveryState(att);
                  const isLatest = idx === attempts.length - 1;
                  const dotColor = state === 'failed' ? 'bg-red-500' : state === 'delivered' ? 'bg-green-500' : state === 'queued' ? 'bg-blue-500' : 'bg-slate-400';
                  return (
                    <div key={att.attempt_id} className="relative flex gap-4">
                      <div className="flex-shrink-0 relative z-10">
                        <div className={`w-8 h-8 rounded-full ${dotColor} flex items-center justify-center`}>
                          <span className="text-xs font-bold text-white">{att.attempt_n}</span>
                        </div>
                      </div>
                      <div className={`flex-1 rounded-lg border p-4 ${isLatest ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200 bg-slate-50/50'}`}>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <AttemptChip n={att.attempt_n} total={attempts.length} />
                          <AttemptDeliveryBadge attempt={att} />
                          {isLatest && (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Latest</span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-slate-600">
                          <div>
                            <span className="font-medium text-slate-500 uppercase">Sent</span>
                            <p className="mt-0.5">{att.attempted_at ? `${formatDate(att.attempted_at)} ${formatTime(att.attempted_at)}` : '--'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-500 uppercase">Result</span>
                            <p className="mt-0.5">
                              {att.send_result?.sent ? 'Sent successfully' : att.send_result?.reason || 'Not sent'}
                            </p>
                          </div>
                          {att.recipients && (att.recipients.to?.length > 0 || att.recipients.cc?.length > 0) && (
                            <div className="md:col-span-2">
                              <span className="font-medium text-slate-500 uppercase">Recipients</span>
                              <p className="mt-0.5">
                                {(att.recipients.to || []).map((r: any) => typeof r === 'string' ? r : r.email).join(', ')}
                                {att.recipients.cc?.length > 0 && (
                                  <span className="text-slate-400"> (CC: {att.recipients.cc.map((r: any) => typeof r === 'string' ? r : r.email).join(', ')})</span>
                                )}
                              </p>
                            </div>
                          )}
                          {att.operator_modifications && Object.keys(att.operator_modifications).length > 0 && (
                            <div className="md:col-span-2">
                              <span className="font-medium text-slate-500 uppercase">Modifications</span>
                              <p className="mt-0.5 text-slate-700">
                                {Object.entries(att.operator_modifications).map(([k, v]) => `${k}: ${v}`).join(', ')}
                              </p>
                            </div>
                          )}
                        </div>
                        {att.delivery_failures && att.delivery_failures.length > 0 && (
                          <DeliveryFailurePanel failures={att.delivery_failures} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Back to queue (bottom) — Dorella's "back to queue on bottom" ask (amos67) */}
      <div className="mt-6 mb-2 flex justify-center">
        <button
          onClick={() => navigate(backToQueueUrl)}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-slate-200 rounded-lg shadow-sm hover:text-blue-800 hover:border-blue-300 transition-colors"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Queue
        </button>
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (() => {
        const attempts = getAttemptsView(request);
        const n = attempts.length;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-slate-800 mb-2">Delete this request?</h3>
              <p className="text-sm text-slate-600 mb-4">
                {n > 1
                  ? `This will hide the request and all ${n} attempts from default views. It can be recovered by an administrator.`
                  : 'This will hide the request from default views. It can be recovered by an administrator.'}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDetailDelete}
                  className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Confirmation dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 max-h-[85vh] flex flex-col">
            <h3 className="text-lg font-bold text-slate-800 mb-2 flex-shrink-0">
              {sendMode === 'manual' ? 'Confirm Approve & Send' : 'Confirm Approval'}
            </h3>
            {request.multi_ref_note && (
              <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 flex-shrink-0">
                <span className="font-semibold">Warning:</span> {request.multi_ref_note}
              </div>
            )}
            {(() => {
              const subject = request.outbound_email?.subject;
              const toList = request.outbound_email?.to?.length
                ? request.outbound_email.to
                : request.response_recipients?.to?.map(r => r.email) || [];
              const ccList = request.outbound_email?.cc?.length
                ? request.outbound_email.cc
                : request.response_recipients?.cc?.map(r => r.email) || [];
              const toDisplay = toList.length > 0 ? toList.join(', ') : request.email_from;
              const ccDisplay = ccList.length > 0 ? ccList.join(', ') : 'None';

              const recipientsEmpty = toList.length === 0;
              const isSenderFallback = request.response_recipients?.extraction_method === 'sender_fallback';
              const fromDomain = request.email_from?.split('@')[1]?.toLowerCase();
              const externalTo = toList.filter(e => e.split('@')[1]?.toLowerCase() !== fromDomain);
              const requesterEcho = toList.length > 0 && externalTo.length === 0;
              const needsAck = isSenderFallback || requesterEcho;
              const sendBlocked = approving || recipientsEmpty || (needsAck && !sendAck);

              return (
                <>
                  <div className="overflow-y-auto min-h-0 flex-1 break-words">
                  {subject && (
                    <>
                      <p className="text-xs font-medium text-slate-500 uppercase mb-1">Subject</p>
                      <p className="text-sm font-semibold text-slate-800 mb-2">{subject}</p>
                    </>
                  )}
                  <p className="text-xs font-medium text-slate-500 uppercase mb-1">To</p>
                  <p className="text-sm text-slate-800 mb-2">{toDisplay}</p>
                  <p className="text-xs font-medium text-slate-500 uppercase mb-1">CC</p>
                  <p className="text-sm text-slate-800 mb-3">{ccDisplay}</p>

                  {recipientsEmpty && (
                    <div className="mb-3 bg-red-50 border border-red-300 rounded-lg px-3 py-2 text-xs text-red-700 flex items-center gap-1.5">
                      <svg className="w-4 h-4 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 11-12.728 0M12 9v4m0 4h.01" />
                      </svg>
                      No recipients — cannot send. Go back and add a To address.
                    </div>
                  )}

                  {needsAck && !recipientsEmpty && (
                    <div className="mb-3 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5 text-xs text-amber-800">
                      <div className="flex items-start gap-1.5 mb-2">
                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span className="font-semibold">
                          {isSenderFallback
                            ? 'No broker address found — recipient was auto-detected from the sender.'
                            : `Sending back to the requester's own domain (${fromDomain}).`}
                        </span>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={sendAck}
                          onChange={e => setSendAck(e.target.checked)}
                          className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                        />
                        <span>I've verified this recipient is correct.</span>
                      </label>
                    </div>
                  )}

                  <button
                    onClick={() => { setShowConfirm(false); document.getElementById('recipients-panel')?.scrollIntoView({ behavior: 'smooth' }); }}
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline mb-3 block"
                  >
                    Wrong recipients? Correct them first &rarr;
                  </button>
                  <p className="text-sm text-slate-600">
                    {sendMode === 'manual'
                      ? 'This will send the loss run document to the recipient above.'
                      : 'Approve this loss run? (Send is disabled — document will be approved but not emailed.)'}
                  </p>
                  </div>
                  <div className="flex justify-end gap-3 flex-shrink-0 pt-3 border-t border-slate-200 mt-3">
                    <button
                      onClick={() => setShowConfirm(false)}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApprove}
                      disabled={sendBlocked}
                      className="px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed rounded-lg transition-colors"
                    >
                      {sendMode === 'manual' ? 'Yes, Approve & Send' : 'Yes, Approve'}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MASTER FILE MANAGER
// ═══════════════════════════════════════════════════════════════════════════════

function MasterFileManager() {
  const [uploads, setUploads] = useState<MasterUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchUploads = useCallback(async () => {
    try {
      const data = await api.getConvexMasterUploads();
      setUploads(data.uploads || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load uploads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUploads(); }, [fetchUploads]);

  const handleFile = async (file: File) => {
    if (!file.name.endsWith('.xlsx')) {
      setError('Only .xlsx files are accepted.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      await api.uploadConvexMaster(file);
      await fetchUploads();
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // The most recent "processed" upload is considered active
  const activeUpload = uploads.find(u => u.status === 'processed') || uploads[0];

  return (
    <div>
      {/* Upload zone */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Upload New Master File</h3>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-blue-400 bg-blue-50'
              : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
          }`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <p className="text-sm text-blue-600 font-medium">Uploading...</p>
            </div>
          ) : (
            <>
              <svg className="w-10 h-10 text-slate-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-slate-600 text-sm font-medium">
                Drag and drop a Convex master Excel file here, or click to browse
              </p>
              <p className="text-slate-400 text-xs mt-1">
                Accepts .xlsx files. The file will be processed and policies consolidated automatically.
              </p>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          onChange={handleInputChange}
          className="hidden"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Upload History */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">Upload History</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center">
            <svg className="animate-spin h-6 w-6 text-blue-500 mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          </div>
        ) : uploads.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            No master files uploaded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Filename</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Uploaded</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-slate-500 uppercase">Rows</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {uploads.map((upload) => {
                  const isActive = activeUpload?.upload_id === upload.upload_id;
                  return (
                    <tr key={upload.upload_id} className={`hover:bg-slate-50 ${isActive ? 'bg-green-50' : ''}`}>
                      <td className="px-4 py-3 text-slate-800 font-medium">{upload.filename}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDate(upload.uploaded_at)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-700">{upload.row_count || '--'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          upload.status === 'processed' ? 'bg-green-100 text-green-700' :
                          upload.status === 'uploaded' ? 'bg-blue-100 text-blue-700' :
                          upload.status === 'processing' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {upload.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {isActive && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            Current
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  FEEDBACK
// ═══════════════════════════════════════════════════════════════════════════════

function FeedbackForm({ onClose }: { onClose: () => void }) {
  const [feedback, setFeedback] = useState('');
  const [refNum, setRefNum] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!feedback.trim()) return;
    setSubmitting(true);
    // Amos-64: durable record binding when the form is opened from a record page
    const reqMatch = window.location.pathname.match(/\/convex\/request\/([0-9a-f-]{36})/i);
    const boundRequestId = reqMatch ? reqMatch[1].toLowerCase() : undefined;
    try {
      await api.saveConvexFeedback({
        feedback: feedback.trim(),
        reference_number: refNum.trim() || undefined,
        page_url: window.location.href,
        request_id: boundRequestId,
      });
      if (boundRequestId) await autoStamp(boundRequestId, 'feedback');
      setSubmitted(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
          <div className="text-center">
            <div className="text-green-600 text-2xl mb-2">&#10003;</div>
            <p className="text-slate-700 font-medium">Feedback submitted. Thank you!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Send Feedback</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">
              Reference Number <span className="text-slate-400">(optional)</span>
            </label>
            <input
              type="text"
              value={refNum}
              onChange={e => setRefNum(e.target.value)}
              placeholder="e.g. XSC000830"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">
              What did you find? <span className="text-red-400">*</span>
            </label>
            <textarea
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              rows={5}
              placeholder="Describe the issue — which entry, what's wrong, what it should look like."
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!feedback.trim() || submitting}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}


//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function ConvexLossRun() {
  const [showFeedback, setShowFeedback] = useState(false);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-slate-800">Loss Run Automation</h1>
        <button
          onClick={() => setShowFeedback(true)}
          className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 border border-slate-300"
        >
          Send Feedback
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Automated loss run request processing — Convex auto-pipeline + manual queue for other insurers
      </p>
      <ConvexNav />
      <Routes>
        <Route index element={<RequestQueue />} />
        <Route path="master" element={<MasterFileManager />} />
        <Route path="request/:requestId" element={<RequestDetail />} />
      </Routes>
      {showFeedback && <FeedbackForm onClose={() => setShowFeedback(false)} />}
    </div>
  );
}
